import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import {
  FullWidthYellowPanel,
  SmallGrayPanel,
  SmallGreenPanel,
  backendPalette,
} from '../../components/backend/BackendCardSystem';
import { usePostCareerJob } from '../../hooks/useCareers';
import {
  useJobBoardData,
  useSaveJobBoardListing,
  useSetJobBoardListingStatus,
  useUpdateJobBoardCandidateLifecycle,
  useUpdateJobBoardListing,
} from '../../hooks/useJobBoard';
import { USERS_PAGE_SIZE } from '../../hooks/useUsers';
import {
  type CandidateApplicationRecord,
  type CandidateApplicationStatus,
  type CareerJob,
  type JobApplicationPhase,
  type JobListingStatus,
  createDefaultJobApplicationPhases,
  getCareerJobApplicationPhases,
  getCareerJobListingStatus,
  normalizeCandidateApplicationStatus,
} from '../../lib/careers/registry';
import type { UserRecord } from '../../api/admin';
import GlassPicklist from '../../components/ui/GlassPicklist';
import PortalAvatar from '../../components/profile/PortalAvatar';
import {
  cyberGlassBackdrop,
  cyberGlassHairline,
  cyberGlassSurface,
  cyberGlassTextMuted,
  cyberGlassTextPrimary,
} from '../../styles/cyberGlass';
import { useApi } from '../../context/ApiProvider';
import { fetchOrganizationTree } from '../../features/skills-tree/api';
import type { SkillsTreeNode } from '../../features/skills-tree/types';

type DetailsTab = 'overview' | 'listing' | 'candidates' | 'lifecycle';
type ListingActionKey = 'pause' | 'reactivate' | 'delete' | 'filled' | 'publish' | 'unpublish';
type ListingActionDescriptor = {
  id: string;
  key: ListingActionKey;
  label: string;
  danger?: boolean;
};

type CreateListingFormState = {
  title: string;
  requisitionId: string;
  team: string;
  brainNodeId: string;
  location: string;
  workType: 'full_time' | 'part_time' | 'contract';
  summary: string;
  description: string;
  responsibilities: string;
  requirements: string;
  hiringManager: string;
  targetHireCount: number;
  published: boolean;
};

type ListingEditForm = {
  title: string;
  requisitionId: string;
  team: string;
  brainNodeId: string;
  location: string;
  workType: 'full_time' | 'part_time' | 'contract';
  summary: string;
  description: string;
  responsibilities: string;
  requirements: string;
};

type OverviewEditForm = {
  hiringManager: string;
  targetHireCount: number;
  compensation: string;
};

type CandidateLifecycleDraft = {
  status: CandidateApplicationStatus;
  phaseId: string;
  reviewer: string;
  notes: string;
  scorecard: string;
  stageReviewerScores: Record<string, Record<string, string>>;
};

type InterviewerStaffOption = {
  value: string;
  label: string;
  name: string;
  email: string;
  roleLabel: string;
  photoUrl: string;
};

type BrainTeamOption = {
  id: string;
  label: string;
  teamLabel: string;
};

const DEFAULT_REVIEW_STAGE_NAME = 'Review application';
const LIFECYCLE_STAGE_LIBRARY = [
  'Phone screen',
  'Video Interview',
  'In person Interview',
  'Panel Interview',
  'Candidate Review',
  'Team Meeting',
  'Decision',
] as const;

const DEFAULT_CREATE_LISTING_FORM: CreateListingFormState = {
  title: '',
  requisitionId: '',
  team: '',
  brainNodeId: '',
  location: 'Remote - US',
  workType: 'full_time',
  summary: '',
  description: '',
  responsibilities: '',
  requirements: '',
  hiringManager: '',
  targetHireCount: 1,
  published: true,
};

const listingStatusOptions: Array<{ value: JobListingStatus; label: string }> = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'paused', label: 'Paused' },
  { value: 'closed_filled', label: 'Closed - Filled' },
  { value: 'deleted', label: 'Deleted' },
];

const LISTING_STATUS_FILTER_OPTIONS: Array<{ value: JobListingStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  ...listingStatusOptions,
];

const CREATE_LISTING_WORK_TYPE_OPTIONS: Array<{ value: CareerJob['workType']; label: string }> = [
  { value: 'full_time', label: 'Full time' },
  { value: 'part_time', label: 'Part time' },
  { value: 'contract', label: 'Contract' },
];

const CREATE_LISTING_PUBLISH_OPTIONS: Array<{ value: 'yes' | 'no'; label: string }> = [
  { value: 'yes', label: 'Yes - publish immediately' },
  { value: 'no', label: 'No - keep draft' },
];

const CANDIDATE_SCORE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Select score' },
  ...Array.from({ length: 10 }, (_, index) => {
    const score = String(index + 1);
    return { value: score, label: score };
  }),
];

const candidateStatusOptions: Array<{
  value: CandidateApplicationStatus;
  label: string;
}> = [
  { value: 'application_received', label: 'Application received' },
  { value: 'in_review', label: 'Application in review' },
  { value: 'rejected', label: 'Application rejected' },
  { value: 'hired', label: 'Hired' },
];

const JOB_BOARD_PAGE_SIZE = 8;

type InterviewLifecycleStatus = 'not_scheduled' | 'scheduled' | 'in_progress' | 'complete';

const Wrap = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.2)};
  max-width: 1280px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(2)};
  min-height: calc(100dvh - 2.5rem);

  @media (max-width: 900px) {
    padding: ${({ theme }) => theme.spacing(1.2)};
  }

  @media (max-width: 767px) {
    min-height: calc(100dvh - 5rem);
  }
`;

const Top = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.45)};
`;

const Heading = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 4vw, 2.8rem);
  line-height: 1;
  font-weight: 900;
  color: ${backendPalette.yellow};
`;

const Subtitle = styled.p`
  margin: 0;
  color: rgba(245, 245, 245, 0.86);
  font-size: 0.95rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: ${({ theme }) => theme.spacing(0.8)};
`;

const SmallMetricGreen = styled(SmallGreenPanel)`
  min-height: 102px;
  display: grid;
  align-content: center;
  gap: 6px;
`;

const SmallMetricGray = styled(SmallGrayPanel)`
  min-height: 102px;
  display: grid;
  align-content: center;
  gap: 6px;
`;

const MetricNumber = styled.div`
  position: relative;
  z-index: 1;
  color: ${backendPalette.black};
  font-weight: 900;
  line-height: 1;
  font-size: 2rem;
`;

const MetricNumberRed = styled(MetricNumber)`
  color: ${backendPalette.red};
`;

const MetricLabel = styled.div`
  position: relative;
  z-index: 1;
  color: rgba(26, 26, 26, 0.82);
  font-size: 0.78rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-weight: 800;
`;

const FilterPanel = styled.section`
  background: transparent;
  border: 0;
  padding: 0;
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.8)};
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(180px, 220px) auto;
  gap: ${({ theme }) => theme.spacing(0.8)};

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 660px) {
    grid-template-columns: 1fr;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 12px;
  border: 1px solid #969696;
  background: transparent;
  color: rgba(236, 240, 247, 0.92);
  padding: 0 14px;
  font-size: 0.95rem;
  font-weight: 600;

  &::placeholder {
    color: rgba(236, 240, 247, 0.62);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border: 3px solid #969696;
    box-shadow: none;
  }
`;

const PrimaryButton = styled.button`
  height: 44px;
  border-radius: 12px;
  border: 1px solid ${backendPalette.black};
  background: #e0a15e;
  color: #1a1a1a;
  padding: 0 14px;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
`;

const SecondaryButton = styled.button`
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: transparent;
  color: rgba(242, 242, 242, 0.92);
  padding: 0 12px;
  font-size: 0.8rem;
  font-weight: 800;
  cursor: pointer;
`;

const DangerButton = styled(SecondaryButton)`
  border: 1px solid rgba(239, 96, 96, 0.5);
  color: rgba(255, 165, 165, 0.94);
`;

const LoadingPanel = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(10, 14, 32, 0.5);
  padding: 22px;
  color: rgba(232, 234, 243, 0.86);
  font-weight: 600;
`;

const JobBoardListSection = styled.section`
  min-height: 0;
  flex: 1;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spacing(0.9)};
`;

const JobBoardListViewport = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
`;

const AccordionList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.7)};
  align-content: start;
`;

const ListingItem = styled(FullWidthYellowPanel)<{ $menuOpen?: boolean }>`
  padding: 0;
  overflow: ${({ $menuOpen }) => ($menuOpen ? 'visible' : 'hidden')};
`;

const ListingHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spacing(0.9)};
  align-items: center;
  padding: ${({ theme }) => theme.spacing(0.9)};
`;

const ListingIdentity = styled.button`
  border: none;
  background: transparent;
  text-align: left;
  display: grid;
  gap: 2px;
  padding: 0;
  cursor: pointer;
  min-width: 0;
`;

const ListingTitle = styled.h3`
  margin: 0;
  color: ${backendPalette.black};
  font-size: 0.95rem;
  font-weight: 900;
`;

const ListingMeta = styled.p`
  margin: 0;
  color: rgba(26, 26, 26, 0.74);
  font-size: 0.78rem;
  font-weight: 700;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const pillTone = {
  green: css`
    background: transparent;
    border: 1px solid rgba(26, 26, 26, 0.36);
    color: rgba(26, 26, 26, 0.86);
  `,
  yellow: css`
    background: transparent;
    border: 1px solid rgba(26, 26, 26, 0.36);
    color: rgba(26, 26, 26, 0.86);
  `,
  red: css`
    background: transparent;
    border: 1px solid rgba(26, 26, 26, 0.36);
    color: ${backendPalette.red};
  `,
  gray: css`
    background: transparent;
    border: 1px solid rgba(26, 26, 26, 0.32);
    color: rgba(26, 26, 26, 0.78);
  `,
};

const Pill = styled.span<{ $tone: keyof typeof pillTone }>`
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  ${({ $tone }) => pillTone[$tone]}
`;

const CandidateStatusPill = styled(Pill)`
  border-color: rgba(255, 255, 255, 0.4);
  color: rgba(242, 242, 242, 0.92);
`;

const ActionMenuShell = styled.div`
  position: relative;
`;

const ActionMenuButton = styled.button`
  height: 32px;
  min-width: 36px;
  border-radius: 10px;
  border: 1px solid rgba(26, 26, 26, 0.34);
  background: transparent;
  color: ${backendPalette.black};
  padding: 0 10px;
  font-size: 1rem;
  font-weight: 900;
  line-height: 1;
  cursor: pointer;
`;

const ActionMenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 5;
  min-width: 180px;
  border-radius: 12px;
  border: 1px solid rgba(26, 26, 26, 0.34);
  background: ${backendPalette.yellow};
  overflow: hidden;
  display: grid;
`;

const ActionMenuItem = styled.button<{ $danger?: boolean }>`
  border: none;
  border-top: 1px solid rgba(26, 26, 26, 0.22);
  background: transparent;
  color: ${({ $danger }) => ($danger ? backendPalette.red : backendPalette.black)};
  text-align: left;
  padding: 10px 12px;
  font-size: 0.8rem;
  font-weight: 800;
  cursor: pointer;

  &:first-child {
    border-top: none;
  }
`;

const ListingBody = styled.div`
  border-top: 1px solid rgba(26, 26, 26, 0.24);
  padding: 0 ${({ theme }) => theme.spacing(0.9)} ${({ theme }) => theme.spacing(0.9)};
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.7)};
`;

const BodySummary = styled.p`
  margin: 0;
  color: rgba(26, 26, 26, 0.86);
  font-size: 0.9rem;
  line-height: 1.5;
`;

const OverviewSummary = styled(BodySummary)`
  color: rgba(248, 250, 255, 0.9);
`;

const PipelineRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const LifecycleTimeline = styled.div`
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: thin;
  scrollbar-color: rgba(26, 26, 26, 0.72) rgba(26, 26, 26, 0.18);

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(26, 26, 26, 0.18);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(26, 26, 26, 0.72);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(26, 26, 26, 0.84);
  }

  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const TimelineTrack = styled.div`
  display: flex;
  align-items: center;
  min-width: max-content;
`;

const TimelineStep = styled.div`
  display: flex;
  align-items: center;
`;

const TimelineNode = styled.div`
  display: grid;
  gap: 5px;
  min-width: 170px;
  padding: 0 2px;
`;

const TimelineNodeHeader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TimelineStageNumber = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 1px solid rgba(26, 26, 26, 0.36);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(26, 26, 26, 0.85);
  font-size: 0.68rem;
  font-weight: 900;
`;

const TimelineStageName = styled.div`
  color: rgba(26, 26, 26, 0.9);
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.2;
`;

const TimelineInterviewer = styled.div`
  color: rgba(26, 26, 26, 0.7);
  font-size: 0.72rem;
  font-weight: 700;
`;

const TimelineConnector = styled.span`
  width: 28px;
  height: 1px;
  border-top: 2px dashed rgba(26, 26, 26, 0.3);
  margin: 0 2px;
`;

const StageChip = styled.span`
  border-radius: 10px;
  border: 1px solid rgba(26, 26, 26, 0.3);
  background: transparent;
  color: rgba(26, 26, 26, 0.82);
  padding: 6px 10px;
  font-size: 0.74rem;
  font-weight: 700;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DetailsButton = styled.button`
  border: 1px solid rgba(26, 26, 26, 0.34);
  border-radius: 999px;
  background: ${backendPalette.black};
  color: ${backendPalette.yellow};
  font-size: 0.78rem;
  font-weight: 900;
  padding: 8px 14px;
  cursor: pointer;
`;

const PaginationPanel = styled.section`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.8)};
  padding: 12px 14px;
  border-radius: 16px;
  border: none;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.96), rgba(18, 20, 25, 0.96));
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.45);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const PageInfo = styled.div`
  color: ${cyberGlassTextMuted};
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  @media (max-width: 720px) {
    justify-self: start;
  }
`;

const PagerGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(0.55)};

  @media (max-width: 720px) {
    justify-self: center;
  }
`;

const PagerButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  min-height: 38px;
  min-width: 102px;
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.04);
  color: ${cyberGlassTextPrimary};
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: rgba(224, 161, 94, 0.55);
    background: rgba(255, 255, 255, 0.07);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, 0.72);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, 0.14);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const PagePill = styled.span`
  justify-self: end;
  padding: 0;
  color: ${cyberGlassTextPrimary};
  font-weight: 800;
  font-size: 0.74rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  @media (max-width: 720px) {
    justify-self: end;
  }
`;

const DrawerBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.55);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
  z-index: 1200;
`;

const Drawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: clamp(420px, 72vw, 820px);
  height: 100dvh;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.96), rgba(18, 20, 25, 0.96));
  color: #e8eaed;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
  box-shadow: -18px 0 48px rgba(0, 0, 0, 0.45);
  transform: translateX(${({ $open }) => ($open ? '0%' : '104%')});
  transition: transform 180ms ease;
  z-index: 1201;
  display: grid;
  grid-template-rows: auto auto 1fr;
  overflow: hidden;
  color-scheme: dark;

  @media (max-width: 860px) {
    width: 100vw;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const DrawerHeader = styled.div`
  padding: 12px;
  display: grid;
  gap: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const DrawerHeaderBar = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
  padding-right: 96px;
`;

const DrawerTitleWrap = styled.div`
  min-width: 0;
  display: grid;
  gap: 4px;
`;

const DrawerEyebrow = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const DrawerTitle = styled.h3`
  margin: 0;
  color: #e8eaed;
  font-size: 1.2rem;
  line-height: 1.1;
  font-weight: 900;
`;

const DrawerSub = styled.p`
  margin: 0;
  color: #9aa4b2;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
`;

const DrawerCloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #cbd2e1;
  display: grid;
  place-items: center;
  font-size: 18px;
  cursor: pointer;
`;

const CloseButton = styled.button`
  height: 36px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: transparent;
  color: rgba(242, 242, 242, 0.95);
  padding: 0 14px;
  font-size: 0.8rem;
  font-weight: 800;
  cursor: pointer;
`;

const Tabs = styled.div`
  display: flex;
  gap: 2px;
  padding: 0 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  min-height: 38px;
  margin-bottom: -1px;
  border-radius: 12px 12px 0 0;
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255, 255, 255, 0.08)')};
  border-bottom-color: transparent;
  background: ${({ $active }) =>
    $active ? 'rgba(224, 161, 94, 0.1)' : 'rgba(255, 255, 255, 0.02)'};
  color: ${({ $active }) => ($active ? '#f8ebd6' : '#cbd2e1')};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.02em;
  padding: 0 14px;
  cursor: pointer;
  white-space: nowrap;
`;

const DrawerBody = styled.div`
  min-height: 0;
  overflow: auto;
  padding: 14px 12px 18px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Panel = styled.section`
  display: grid;
  align-content: start;
  align-self: start;
  overflow: auto;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  padding: 12px;
  gap: 10px;
`;

const PanelTitle = styled.h4`
  margin: 0;
  color: #9aa4b2;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.18px;
  font-weight: 900;
`;

const drawerField = css`
  width: 100%;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #e8eaed;
  outline: none;
  box-shadow: none;
  font-weight: 700;

  &::placeholder {
    color: rgba(154, 164, 178, 0.82);
  }

  &:hover:not(:focus-visible) {
    border-style: dashed;
    border-color: rgba(255, 255, 255, 0.28);
  }

  &:focus-visible {
    background: rgba(255, 255, 255, 0.03);
    border-style: solid;
    border-color: transparent;
    box-shadow: inset 0 0 0 1px rgba(138, 180, 248, 0.75);
    outline: 2px solid rgba(138, 180, 248, 0.35);
    outline-offset: 1px;
  }
`;

const InputGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.18px;
`;

const FieldGroup = styled.div`
  display: grid;
  gap: 6px;
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.18px;
`;

const Input = styled.input`
  ${drawerField}
  min-height: 40px;
  padding: 0 11px;
  font-size: 13px;

  option {
    background: #14161c;
    color: #e8eaed;
  }
`;

const Textarea = styled.textarea`
  ${drawerField}
  min-height: 100px;
  padding: 10px 11px;
  font-size: 13px;
  resize: vertical;
  font-family: inherit;
`;

const InlineActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const LifecycleHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const LifecycleHeaderCopy = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const LifecycleStagesViewport = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
`;

const LifecycleStagesStack = styled.div`
  display: grid;
  gap: 10px;
`;

const CandidateCard = styled.article`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.04);
  overflow: hidden;
`;

const CandidateHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
`;

const CandidateHeaderButton = styled.button`
  flex: 1;
  width: auto;
  min-width: 0;
  border: 0;
  margin: 0;
  padding: 0;
  background: transparent;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
`;

const CandidateIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const CandidateAvatar = styled.span`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px dashed rgba(255, 255, 255, 0.5);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(242, 242, 242, 0.95);
  font-size: 0.75rem;
  font-weight: 900;
`;

const CandidateName = styled.div`
  color: rgba(242, 242, 242, 0.95);
  font-size: 0.9rem;
  font-weight: 900;
`;

const CandidateEmail = styled.div`
  color: rgba(236, 240, 247, 0.76);
  font-size: 0.78rem;
  font-weight: 600;
`;

const CandidateHeaderRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const CandidateChevron = styled.span<{ $expanded: boolean }>`
  color: rgba(248, 250, 255, 0.88);
  font-size: 0.8rem;
  font-weight: 900;
  transform: rotate(${({ $expanded }) => ($expanded ? 180 : 0)}deg);
  transition: transform 140ms ease;
`;

const CandidateBody = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  padding: 10px;
  display: grid;
  gap: 10px;
`;

const CandidateStageTimeline = styled.div`
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.9) transparent;

  &::-webkit-scrollbar {
    height: 10px;
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.9);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 1);
  }

  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
    background: transparent;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const CandidateStageTrack = styled.div`
  display: flex;
  align-items: center;
  min-width: max-content;
`;

const CandidateStageSegment = styled.div`
  display: flex;
  align-items: center;
`;

const CandidateStageNode = styled.div`
  display: grid;
  gap: 4px;
  min-width: 150px;
`;

const CandidateStageHeader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const CandidateStageDot = styled.span<{ $state: 'completed' | 'current' | 'remaining' | 'rejected' }>`
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1px solid
    ${({ $state }) =>
      $state === 'completed'
        ? 'rgba(126, 211, 126, 0.88)'
        : $state === 'current'
        ? 'rgba(224, 161, 94, 0.92)'
        : $state === 'rejected'
        ? 'rgba(239, 96, 96, 0.9)'
        : 'rgba(255, 255, 255, 0.34)'};
  background:
    ${({ $state }) =>
      $state === 'completed'
        ? 'rgba(126, 211, 126, 0.2)'
        : $state === 'current'
        ? 'rgba(224, 161, 94, 0.2)'
        : $state === 'rejected'
        ? 'rgba(239, 96, 96, 0.22)'
        : 'rgba(255, 255, 255, 0.06)'};
`;

const CandidateStageName = styled.div`
  color: rgba(248, 250, 255, 0.94);
  font-size: 0.76rem;
  font-weight: 800;
`;

const CandidateStageMeta = styled.div`
  color: ${cyberGlassTextMuted};
  font-size: 0.72rem;
  font-weight: 700;
`;

const CandidateStageScore = styled.div`
  color: rgba(248, 250, 255, 0.84);
  font-size: 0.7rem;
  font-weight: 800;
`;

const CandidateStageConnector = styled.span<{ $active: boolean }>`
  width: 26px;
  height: 1px;
  border-top: 2px dashed
    ${({ $active }) => ($active ? 'rgba(126, 211, 126, 0.55)' : 'rgba(255, 255, 255, 0.24)')};
  margin: 0 4px;
`;

const CandidateTimelineTotalWrap = styled.div`
  display: flex;
  align-items: center;
`;

const CandidateTimelineTotalNode = styled.div`
  display: grid;
  gap: 4px;
  min-width: 100px;
`;

const CandidateTimelineTotalCircle = styled.span`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid rgba(224, 161, 94, 0.92);
  background: rgba(224, 161, 94, 0.2);
  color: rgba(248, 250, 255, 0.96);
  font-size: 0.78rem;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CandidateGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
`;

const CandidateScorecards = styled.div`
  display: grid;
  gap: 8px;
`;

const CandidateScorecardRow = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) minmax(110px, 140px);
  align-items: center;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const CandidateInterviewerLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const CandidateInterviewerName = styled.span`
  color: rgba(248, 250, 255, 0.94);
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1.2;
  word-break: break-word;
`;

const CandidateTopActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const CandidateHiredChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  border-radius: 10px;
  border: 0;
  background: rgba(126, 211, 126, 0.2);
  color: rgba(186, 243, 186, 0.98);
  padding: 0 12px;
  font-size: 0.8rem;
  font-weight: 800;
  cursor: default;
  user-select: none;
`;

const CandidateDecisionFooter = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CandidateDecisionFooterRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const CandidateRejectButton = styled(DangerButton)`
  margin-left: 0;
`;

const CandidateMeta = styled.div`
  color: rgba(236, 240, 247, 0.8);
  font-size: 0.75rem;
  font-weight: 700;
`;

const CandidateDetailsGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const CandidateDetailItem = styled.div`
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.03);
  padding: 8px 10px;
  display: grid;
  gap: 2px;
`;

const CandidateDetailLabel = styled.span`
  color: ${cyberGlassTextMuted};
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const CandidateDetailValue = styled.span`
  color: rgba(248, 250, 255, 0.92);
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.3;
  word-break: break-word;
`;

const FileLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FileLink = styled.a`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: transparent;
  color: rgba(242, 242, 242, 0.94);
  text-decoration: none;
  padding: 4px 10px;
  font-size: 0.72rem;
  font-weight: 800;
`;

const EmptyState = styled.div`
  border-radius: 12px;
  border: 1px dashed rgba(255, 255, 255, 0.28);
  padding: 14px;
  color: rgba(236, 240, 247, 0.76);
  font-size: 0.85rem;
  font-weight: 700;
`;

const PhaseCard = styled.article`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.04);
  padding: 10px;
  display: grid;
  gap: 8px;
`;

const PhaseHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const PhaseHeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PhaseMoveButton = styled(SecondaryButton)`
  height: 30px;
  padding: 0 10px;
  font-size: 0.74rem;
`;

const PhaseTitle = styled.h5`
  margin: 0;
  color: rgba(242, 242, 242, 0.95);
  font-size: 0.84rem;
  font-weight: 900;
`;

const StageFixedName = styled.div`
  min-height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: transparent;
  color: rgba(248, 248, 248, 0.95);
  padding: 0 11px;
  display: flex;
  align-items: center;
  font-size: 0.88rem;
  font-weight: 700;
`;

const PhaseInterviewersShell = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
`;

const PhaseInterviewersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PhaseInterviewerChip = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const PhaseInterviewerAvatar = styled(PortalAvatar)`
  flex: 0 0 auto;
`;

const PhaseInterviewerRemove = styled.button`
  all: unset;
  box-sizing: border-box;
  position: absolute;
  top: -4px;
  right: -4px;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.38);
  background: rgba(24, 28, 40, 0.95);
  color: rgba(248, 250, 255, 0.94);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(248, 250, 255, 0.94);
  font-size: 0.6rem;
  font-weight: 900;
`;

const PhaseAddInterviewerButton = styled.button<{ $active: boolean }>`
  all: unset;
  box-sizing: border-box;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: 1.5px dashed
    ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.78)' : 'rgba(255, 255, 255, 0.42)')};
  color: rgba(248, 250, 255, 0.94);
  font-size: 1.2rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const PhaseInterviewerMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: min(340px, 86vw);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.26);
  background: rgba(16, 20, 31, 0.88);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 16px 30px rgba(0, 0, 0, 0.35);
  z-index: 40;
  padding: 8px;
  display: grid;
  gap: 8px;
`;

const PhaseInterviewerSearch = styled.input`
  min-height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(248, 250, 255, 0.96);
  padding: 0 10px;
  font-size: 0.8rem;
  font-weight: 700;

  &::placeholder {
    color: rgba(248, 250, 255, 0.62);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(255, 255, 255, 0.72);
  }
`;

const PhaseInterviewerList = styled.div`
  display: grid;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
`;

const PhaseInterviewerOption = styled.button`
  all: unset;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.04);
  padding: 7px 9px;
  cursor: pointer;
  display: grid;
  gap: 2px;

  &:hover,
  &:focus-visible {
    border-color: rgba(255, 255, 255, 0.44);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const PhaseInterviewerOptionTop = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const PhaseInterviewerOptionName = styled.span`
  color: rgba(248, 250, 255, 0.96);
  font-size: 0.8rem;
  font-weight: 900;
`;

const PhaseInterviewerOptionMeta = styled.span`
  color: ${cyberGlassTextMuted};
  font-size: 0.73rem;
  font-weight: 700;
`;

const PhaseInterviewerEmpty = styled.span`
  border-radius: 10px;
  border: 1px dashed rgba(255, 255, 255, 0.3);
  padding: 9px 10px;
  color: rgba(248, 250, 255, 0.8);
  font-size: 0.78rem;
  font-weight: 700;
`;

const FooterMeta = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 12px;
  font-weight: 700;
`;

const OverviewGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const OverviewCard = styled.article<{ $spanTwo?: boolean }>`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  padding: 10px 12px;
  display: grid;
  gap: 4px;
  min-height: 84px;
  align-content: center;
  ${({ $spanTwo }) =>
    $spanTwo
      ? css`
          grid-column: span 2;

          @media (max-width: 620px) {
            grid-column: auto;
          }
        `
      : ''}
`;

const OverviewLabel = styled.span`
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.18px;
`;

const OverviewValue = styled.span`
  color: #e8eaed;
  font-size: 1.2rem;
  font-weight: 900;
  line-height: 1.15;
`;

const OverviewSub = styled.span`
  color: rgba(186, 198, 224, 0.82);
  font-size: 12px;
  font-weight: 700;
`;

const OverviewFieldInput = styled.input`
  ${drawerField}
  min-height: 38px;
  padding: 0 11px;
  font-size: 13px;
`;

const ModalBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  ${cyberGlassBackdrop};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
  z-index: 200;
`;

const Modal = styled.section<{ $open: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(${({ $open }) => ($open ? 1 : 0.98)});
  width: min(720px, calc(100vw - 28px));
  max-height: min(760px, calc(100vh - 42px));
  border-radius: 16px;
  ${cyberGlassSurface};
  border: 1px solid ${cyberGlassHairline};
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.42);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease, transform 180ms ease;
  z-index: 210;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const ModalHeader = styled.div`
  padding: ${({ theme }) => theme.spacing(0.9)};
  border-bottom: 1px solid ${cyberGlassHairline};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(0.7)};
`;

const ModalHeaderCopy = styled.div`
  display: grid;
  gap: 2px;
`;

const ModalTitle = styled.div`
  margin: 0;
  color: ${cyberGlassTextPrimary};
  font-size: 1rem;
  line-height: 1.2;
  font-weight: 900;
`;

const ModalSub = styled.div`
  margin: 0;
  color: ${cyberGlassTextMuted};
  font-size: 0.82rem;
  font-weight: 700;
`;

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing(0.9)};
  overflow: auto;
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.7)};
  align-content: start;
`;

const CreateListingForm = styled.form`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.7)};
`;

const CreateListingField = styled(Field)`
  color: rgba(248, 250, 255, 0.96);
`;

const CreateListingInput = styled(Input)`
  border: 1px solid rgba(255, 255, 255, 0.9);
  background: transparent;
  color: rgba(248, 250, 255, 0.96);

  &::placeholder {
    color: rgba(248, 250, 255, 0.58);
  }

  &:focus,
  &:focus-visible {
    border-color: rgba(255, 255, 255, 1);
  }
`;

const CreateListingTextarea = styled(Textarea)`
  border: 1px solid rgba(255, 255, 255, 0.9);
  background: transparent;
  color: rgba(248, 250, 255, 0.96);

  &::placeholder {
    color: rgba(248, 250, 255, 0.58);
  }

  &:focus,
  &:focus-visible {
    border-color: rgba(255, 255, 255, 1);
  }
`;

const CreateListingHelperText = styled.div<{ $error?: boolean }>`
  color: ${({ $error }) => ($error ? '#ffb8b2' : 'rgba(248, 250, 255, 0.88)')};
  font-size: 0.8rem;
  font-weight: 700;
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${({ $error }) => ($error ? '#fecaca' : 'rgba(186, 198, 224, 0.82)')};
  font-size: 12px;
  font-weight: 800;
`;

const CreateListingSubmitButton = styled.button`
  border-radius: 10px;
  min-height: 40px;
  border: 1px solid rgba(26, 26, 26, 0.34);
  background: ${backendPalette.red};
  color: ${backendPalette.black};
  font-size: 0.8rem;
  font-weight: 900;
  padding: 0 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function splitMultilineList(value: string): string[] {
  return value
    .split(/\r?\n/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function joinMultilineList(value: string[]): string {
  return value.join('\n');
}

function formatListingStatus(status: JobListingStatus): string {
  const item = listingStatusOptions.find((option) => option.value === status);
  return item?.label ?? status;
}

function formatWorkType(value: CareerJob['workType']): string {
  if (value === 'part_time') return 'Part time';
  if (value === 'contract') return 'Contract';
  return 'Full time';
}

function getListingToggleActions(status: JobListingStatus): ListingActionDescriptor[] {
  const actions: ListingActionDescriptor[] = [];

  if (status === 'published') {
    actions.push({ id: 'pause-toggle', key: 'pause', label: 'Pause' });
  } else if (status === 'paused') {
    actions.push({ id: 'pause-toggle', key: 'reactivate', label: 'Reactivate' });
  }

  if (status === 'published') {
    actions.push({ id: 'publish-toggle', key: 'unpublish', label: 'Unpublish' });
  } else if (status !== 'deleted') {
    actions.push({ id: 'publish-toggle', key: 'publish', label: 'Publish' });
  }

  if (status === 'closed_filled') {
    actions.push({ id: 'filled-toggle', key: 'reactivate', label: 'Reactivate' });
  } else if (status !== 'deleted') {
    actions.push({ id: 'filled-toggle', key: 'filled', label: 'Filled' });
  }

  if (status !== 'deleted') {
    actions.push({ id: 'delete', key: 'delete', label: 'Delete', danger: true });
  }

  return actions;
}

function listingEditFormFromJob(job: CareerJob): ListingEditForm {
  return {
    title: job.title,
    requisitionId: job.requisitionId,
    team: job.team,
    brainNodeId: job.brainNodeId ?? '',
    location: job.location,
    workType: job.workType,
    summary: job.summary,
    description: job.description,
    responsibilities: joinMultilineList(job.responsibilities),
    requirements: joinMultilineList(job.requirements),
  };
}

function asBrainMetaText(node: SkillsTreeNode, key: string): string {
  const value = node.meta?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

function buildBrainTeamOptions(nodes: SkillsTreeNode[]): BrainTeamOption[] {
  return nodes
    .filter((node) => node.status !== 'archived')
    .map((node) => {
      const team = asBrainMetaText(node, 'team');
      if (!team) return null;
      const position = asBrainMetaText(node, 'position');
      const teamLabel = team;
      const labelParts = [teamLabel];
      if (position) labelParts.push(position);
      if (node.title.trim() && node.title.trim() !== teamLabel) {
        labelParts.push(node.title.trim());
      }
      return {
        id: node.id,
        teamLabel,
        label: labelParts.join(' / '),
      } satisfies BrainTeamOption;
    })
    .filter((option): option is BrainTeamOption => Boolean(option?.teamLabel))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function initials(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (value.slice(0, 2) || 'CA').toUpperCase();
}

function stageIdSuffix(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function clampScoreTarget(value: number | null): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.round(value);
  if (rounded < 1) return 1;
  if (rounded > 10) return 10;
  return rounded;
}

function parseScoreInput(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return '';
  const bounded = Math.max(1, Math.min(10, Math.round(parsed)));
  return String(bounded);
}

function normalizeInterviewLifecycleStatus(
  value: string | null | undefined,
): InterviewLifecycleStatus {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized.includes('progress')) return 'in_progress';
  if (normalized.includes('schedule') && !normalized.includes('not')) return 'scheduled';
  if (
    normalized.includes('complete') ||
    normalized.includes('completed') ||
    normalized.includes('hired') ||
    normalized.includes('reject')
  ) {
    return 'complete';
  }
  if (normalized.includes('not') && normalized.includes('schedule')) return 'not_scheduled';
  return 'not_scheduled';
}

function normalizeTargetHireCount(
  value: number | null | undefined,
  fallback = 1,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  const rounded = Math.round(value);
  if (rounded < 1) return 1;
  if (rounded > 99) return 99;
  return rounded;
}

function normalizeExternalLink(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function parseInterviewerList(value: string): string[] {
  if (!value.trim()) return [];
  return value
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function joinInterviewerList(values: string[]): string {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(trimmed);
  }
  return normalized.join('\n');
}

function phaseInterviewersById(phases: JobApplicationPhase[]): Record<string, string[]> {
  const byId: Record<string, string[]> = {};
  for (const phase of phases) {
    byId[phase.id] = parseInterviewerList(phase.interviewer);
  }
  return byId;
}

function getStageReviewerScore(
  stageReviewerScores: Record<string, string>,
  interviewer: string,
): string {
  const direct = stageReviewerScores[interviewer];
  if (typeof direct === 'string') return direct;
  const normalizedKey = interviewer.trim().toLowerCase();
  if (!normalizedKey) return '';
  for (const [key, value] of Object.entries(stageReviewerScores)) {
    if (key.trim().toLowerCase() === normalizedKey) {
      return value;
    }
  }
  return '';
}

function stageScoreValues(
  stageReviewerScores: Record<string, string>,
  interviewers: string[],
): number[] {
  const values: number[] = [];
  for (const interviewer of interviewers) {
    const raw = getStageReviewerScore(stageReviewerScores, interviewer);
    const parsed = Number.parseInt(parseScoreInput(raw ?? ''), 10);
    if (Number.isFinite(parsed)) {
      values.push(parsed);
    }
  }
  return values;
}

function stageScoreSubmissionCount(
  stageReviewerScores: Record<string, string>,
  interviewers: string[],
): number {
  return stageScoreValues(stageReviewerScores, interviewers).length;
}

function stageScoreAverage(
  stageReviewerScores: Record<string, string>,
  interviewers: string[],
): number | null {
  if (interviewers.length === 0) return null;
  const values = stageScoreValues(stageReviewerScores, interviewers);
  if (values.length !== interviewers.length) return null;
  return values.reduce((sum, score) => sum + score, 0) / values.length;
}

function formatScore(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '--';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
}

function resolvePhaseIndex(phases: JobApplicationPhase[], phaseId: string): number {
  if (!phases.length) return -1;
  const index = phases.findIndex((phase) => phase.id === phaseId);
  return index >= 0 ? index : 0;
}

function normalizePhaseDrafts(
  phases: JobApplicationPhase[],
  seed: string,
): JobApplicationPhase[] {
  const mapped = phases
    .map((phase, index) => ({
      id: phase.id?.trim() || `phase_${index + 1}_${stageIdSuffix(seed)}`,
      name:
        index === 0
          ? DEFAULT_REVIEW_STAGE_NAME
          : phase.name?.trim() || `Stage ${index + 1}`,
      order: index + 1,
      interviewer: phase.interviewer?.trim() || '',
      notes: phase.notes?.trim() || '',
      scorecard: null,
    }))
    .filter((phase) => phase.name.length > 0);

  if (!mapped.length) return createDefaultJobApplicationPhases(seed);

  const [first, ...rest] = mapped;
  return [
    {
      ...first,
      id: first.id || `phase_review_${stageIdSuffix(seed)}`,
      name: DEFAULT_REVIEW_STAGE_NAME,
      order: 1,
    },
    ...rest.map((phase, index) => ({
      ...phase,
      name: phase.name?.trim() || `Stage ${index + 2}`,
      order: index + 2,
    })),
  ];
}

function candidateDraftFromRecord(
  record: CandidateApplicationRecord,
  phases: JobApplicationPhase[],
): CandidateLifecycleDraft {
  const defaultPhaseId = phases[0]?.id ?? '';
  const currentPhaseId = record.currentPhaseId || defaultPhaseId;
  const currentStageReviewers = parseInterviewerList(record.reviewer || '');
  const stageReviewerScores: Record<string, Record<string, string>> = {};
  const persistedStageScores = record.stageReviewerScores;
  if (persistedStageScores && typeof persistedStageScores === 'object') {
    for (const [phaseId, rawStageScores] of Object.entries(persistedStageScores)) {
      if (!phaseId.trim()) continue;
      if (!rawStageScores || typeof rawStageScores !== 'object') continue;
      const normalizedStage: Record<string, string> = {};
      for (const [reviewer, rawScore] of Object.entries(rawStageScores)) {
        const reviewerKey = reviewer.trim();
        if (!reviewerKey) continue;
        const parsed = parseScoreInput(String(rawScore ?? ''));
        if (!parsed) continue;
        normalizedStage[reviewerKey] = parsed;
      }
      if (Object.keys(normalizedStage).length > 0) {
        stageReviewerScores[phaseId] = normalizedStage;
      }
    }
  }
  if (
    currentPhaseId &&
    currentStageReviewers.length > 0 &&
    typeof record.phaseScorecard === 'number' &&
    Number.isFinite(record.phaseScorecard) &&
    !stageReviewerScores[currentPhaseId]
  ) {
    // Preserve legacy total score by assigning it to the first reviewer slot.
    stageReviewerScores[currentPhaseId] = {
      [currentStageReviewers[0]]: parseScoreInput(String(record.phaseScorecard)),
    };
  }

  return {
    status: normalizeCandidateApplicationStatus(record.status),
    phaseId: currentPhaseId,
    reviewer: record.reviewer || '',
    notes: record.phaseNotes || '',
    scorecard:
      typeof record.phaseScorecard === 'number' && Number.isFinite(record.phaseScorecard)
        ? String(clampScoreTarget(record.phaseScorecard) ?? '')
        : '',
    stageReviewerScores,
  };
}

export default function JobBoardPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const boardQuery = useJobBoardData();
  const postCareerJob = usePostCareerJob();
  const updateListing = useUpdateJobBoardListing();
  const saveListing = useSaveJobBoardListing();
  const setListingStatus = useSetJobBoardListingStatus();
  const updateCandidateLifecycle = useUpdateJobBoardCandidateLifecycle();

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobListingStatus | 'all'>('all');
  const [page, setPage] = useState(0);
  const [expandedListingId, setExpandedListingId] = useState<string | null>(null);
  const [openListingActionsId, setOpenListingActionsId] = useState<string | null>(null);
  const [detailsListingId, setDetailsListingId] = useState<string | null>(null);
  const [detailsTab, setDetailsTab] = useState<DetailsTab>('overview');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateListingFormState>(DEFAULT_CREATE_LISTING_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createMessage, setCreateMessage] = useState<string | null>(null);
  const [detailsMessage, setDetailsMessage] = useState<string | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [listingForm, setListingForm] = useState<ListingEditForm | null>(null);
  const [overviewForm, setOverviewForm] = useState<OverviewEditForm | null>(null);
  const [brainTeamOptions, setBrainTeamOptions] = useState<BrainTeamOption[]>([]);
  const [brainTeamOptionsLoading, setBrainTeamOptionsLoading] = useState(true);
  const [brainTeamOptionsError, setBrainTeamOptionsError] = useState<string | null>(null);
  const [phaseDrafts, setPhaseDrafts] = useState<JobApplicationPhase[]>([]);
  const [candidateDrafts, setCandidateDrafts] = useState<Record<string, CandidateLifecycleDraft>>(
    {},
  );
  const [candidateSavingId, setCandidateSavingId] = useState<string | null>(null);
  const [expandedCandidateId, setExpandedCandidateId] = useState<string | null>(null);
  const [openPhaseInterviewerPickerId, setOpenPhaseInterviewerPickerId] = useState<string | null>(
    null,
  );
  const [phaseInterviewerQuery, setPhaseInterviewerQuery] = useState('');
  const staffUsersQuery = useQuery<UserRecord[]>({
    queryKey: ['job-board-staff-users'],
    queryFn: async () => {
      const seen = new Set<string>();
      const rows: UserRecord[] = [];
      for (const role of ['admin', 'agent'] as const) {
        for (let pageIndex = 0; pageIndex < 50; pageIndex += 1) {
          const response = await api.get('/admin/users', {
            params: { q: '', role, page: pageIndex },
          });
          const users = Array.isArray(response.data?.users)
            ? (response.data.users as UserRecord[])
            : [];

          for (const user of users) {
            const key = user.id || user.email;
            if (!key || seen.has(key)) continue;
            seen.add(key);
            rows.push(user);
          }

          if (users.length < USERS_PAGE_SIZE) break;
        }
      }
      return rows;
    },
    staleTime: 2 * 60 * 1000,
  });

  const jobs = useMemo(() => boardQuery.data?.jobs ?? [], [boardQuery.data?.jobs]);
  const candidates = useMemo(
    () => boardQuery.data?.candidates ?? [],
    [boardQuery.data?.candidates],
  );
  const interviewerStaffOptions = useMemo(() => {
    const merged = staffUsersQuery.data ?? [];
    const options: InterviewerStaffOption[] = [];

    for (const user of merged) {
      const email = user.email?.trim();
      if (!email) continue;
      const name = (user.name ?? '').trim() || email;
      const userAny = user as UserRecord & {
        photoURL?: string | null;
        avatarUrl?: string | null;
        avatar?: string | null;
      };
      const photoUrl =
        (typeof userAny.photoURL === 'string' ? userAny.photoURL : '') ||
        (typeof userAny.avatarUrl === 'string' ? userAny.avatarUrl : '') ||
        (typeof userAny.avatar === 'string' ? userAny.avatar : '');
      const roleLabel = user.role.toUpperCase();
      options.push({
        value: email,
        label: `${name} - ${roleLabel}`,
        name,
        email,
        roleLabel,
        photoUrl: photoUrl.trim(),
      });
    }

    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [staffUsersQuery.data]);

  const interviewerDirectory = useMemo(() => {
    const map = new Map<string, InterviewerStaffOption>();
    for (const option of interviewerStaffOptions) {
      map.set(option.value.toLowerCase(), option);
    }
    return map;
  }, [interviewerStaffOptions]);

  const stageInterviewerOptions = useMemo(
    () => interviewerStaffOptions,
    [interviewerStaffOptions],
  );

  useEffect(() => {
    let cancelled = false;

    const loadBrainTeams = async () => {
      try {
        setBrainTeamOptionsLoading(true);
        setBrainTeamOptionsError(null);
        const tree = await fetchOrganizationTree();
        if (cancelled) return;
        setBrainTeamOptions(buildBrainTeamOptions(tree.nodes));
      } catch (error: any) {
        if (cancelled) return;
        setBrainTeamOptions([]);
        setBrainTeamOptionsError(error?.message || 'Failed to load Brain team nodes.');
      } finally {
        if (!cancelled) {
          setBrainTeamOptionsLoading(false);
        }
      }
    };

    void loadBrainTeams();
    return () => {
      cancelled = true;
    };
  }, []);

  const brainTeamOptionById = useMemo(
    () => new Map(brainTeamOptions.map((option) => [option.id, option] as const)),
    [brainTeamOptions],
  );

  const filteredListings = useMemo(() => {
    const normalizedQ = q.trim().toLowerCase();
    return jobs.filter((job) => {
      const status = getCareerJobListingStatus(job);
      if (status === 'deleted' && statusFilter !== 'deleted') return false;
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (!normalizedQ) return true;
      const haystack = `${job.title} ${job.requisitionId} ${job.team} ${job.location}`.toLowerCase();
      return haystack.includes(normalizedQ);
    });
  }, [jobs, q, statusFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredListings.length / JOB_BOARD_PAGE_SIZE)),
    [filteredListings.length],
  );

  useEffect(() => {
    setPage((current) => {
      const maxPageIndex = Math.max(0, totalPages - 1);
      return current > maxPageIndex ? maxPageIndex : current;
    });
  }, [totalPages]);

  const paginatedListings = useMemo(() => {
    const start = page * JOB_BOARD_PAGE_SIZE;
    return filteredListings.slice(start, start + JOB_BOARD_PAGE_SIZE);
  }, [filteredListings, page]);

  const stats = useMemo(() => {
    const active = jobs.filter((job) => getCareerJobListingStatus(job) === 'published').length;
    const paused = jobs.filter((job) => getCareerJobListingStatus(job) === 'paused').length;
    const closed = jobs.filter((job) => getCareerJobListingStatus(job) === 'closed_filled').length;
    const totalCandidates = candidates.length;
    const hired = candidates.filter((candidate) => normalizeCandidateApplicationStatus(candidate.status) === 'hired').length;
    const inReview = candidates.filter((candidate) => normalizeCandidateApplicationStatus(candidate.status) === 'in_review').length;

    return {
      totalListings: jobs.filter((job) => getCareerJobListingStatus(job) !== 'deleted').length,
      active,
      paused,
      closed,
      totalCandidates,
      hired,
      inReview,
    };
  }, [jobs, candidates]);

  const selectedJob = useMemo(
    () => jobs.find((job) => job.id === detailsListingId) ?? null,
    [jobs, detailsListingId],
  );

  const selectedPhases = useMemo(() => {
    if (!selectedJob) return [] as JobApplicationPhase[];
    return getCareerJobApplicationPhases(selectedJob);
  }, [selectedJob]);

  const selectedCandidates = useMemo(() => {
    if (!selectedJob) return [] as CandidateApplicationRecord[];
    const seen = new Set<string>();
    const rows = candidates.filter((candidate) => {
      if (
        candidate.jobId !== selectedJob.id &&
        candidate.requisitionId.toLowerCase() !== selectedJob.requisitionId.toLowerCase()
      ) {
        return false;
      }
      if (seen.has(candidate.id)) return false;
      seen.add(candidate.id);
      return true;
    });
    return rows.sort((a, b) => b.updatedAt - a.updatedAt);
  }, [candidates, selectedJob]);

  useEffect(() => {
    if (!selectedJob) {
      setListingForm(null);
      setOverviewForm(null);
      setPhaseDrafts([]);
      setExpandedCandidateId(null);
      setOpenPhaseInterviewerPickerId(null);
      setPhaseInterviewerQuery('');
      return;
    }
    setListingForm(listingEditFormFromJob(selectedJob));
    setOverviewForm({
      hiringManager: (selectedJob.hiringManager ?? '').trim(),
      targetHireCount: normalizeTargetHireCount(selectedJob.targetHireCount, 1),
      compensation: (selectedJob.compensation ?? '').trim(),
    });
    setPhaseDrafts(getCareerJobApplicationPhases(selectedJob));
    setExpandedCandidateId(null);
    setOpenPhaseInterviewerPickerId(null);
    setPhaseInterviewerQuery('');
  }, [selectedJob]);

  useEffect(() => {
    if (!openPhaseInterviewerPickerId) return;
    if (phaseDrafts.some((phase) => phase.id === openPhaseInterviewerPickerId)) return;
    setOpenPhaseInterviewerPickerId(null);
    setPhaseInterviewerQuery('');
  }, [openPhaseInterviewerPickerId, phaseDrafts]);

  useEffect(() => {
    if (!openPhaseInterviewerPickerId) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        setOpenPhaseInterviewerPickerId(null);
        setPhaseInterviewerQuery('');
        return;
      }
      if (target.closest('[data-interviewer-shell="true"]')) return;
      setOpenPhaseInterviewerPickerId(null);
      setPhaseInterviewerQuery('');
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpenPhaseInterviewerPickerId(null);
      setPhaseInterviewerQuery('');
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openPhaseInterviewerPickerId]);

  useEffect(() => {
    if (!selectedJob) {
      setCandidateDrafts({});
      return;
    }
    const phases = getCareerJobApplicationPhases(selectedJob);
    const next: Record<string, CandidateLifecycleDraft> = {};
    for (const candidate of selectedCandidates) {
      next[candidate.id] = candidateDraftFromRecord(candidate, phases);
    }
    setCandidateDrafts(next);
  }, [selectedCandidates, selectedJob]);

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateForm(DEFAULT_CREATE_LISTING_FORM);
    setCreateError(null);
    setCreateMessage(null);
  };

  const closeDetails = () => {
    setDetailsListingId(null);
    setDetailsTab('overview');
    setOpenListingActionsId(null);
    setDetailsError(null);
    setDetailsMessage(null);
  };

  const createListing = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const title = createForm.title.trim();
    const requisitionId = createForm.requisitionId.trim();
    const brainNodeId = createForm.brainNodeId.trim();
    const team =
      brainTeamOptionById.get(brainNodeId)?.teamLabel ??
      createForm.team.trim() ??
      'General';
    const location = createForm.location.trim() || 'Remote';
    const summary = createForm.summary.trim();
    const description = createForm.description.trim();

    if (!title) {
      setCreateError('Job title is required.');
      setCreateMessage(null);
      return;
    }
    if (title.length < 2 || title.length > 180) {
      setCreateError('Job title must be between 2 and 180 characters.');
      setCreateMessage(null);
      return;
    }
    if (!requisitionId) {
      setCreateError('Requisition number is required.');
      setCreateMessage(null);
      return;
    }
    if (requisitionId.length < 2 || requisitionId.length > 120) {
      setCreateError('Requisition number must be between 2 and 120 characters.');
      setCreateMessage(null);
      return;
    }
    if (!brainNodeId) {
      setCreateError('Select the Brain team node responsible for this role.');
      setCreateMessage(null);
      return;
    }
    if (team.length < 2 || team.length > 120) {
      setCreateError('Team must be between 2 and 120 characters.');
      setCreateMessage(null);
      return;
    }
    if (location.length < 2 || location.length > 120) {
      setCreateError('Location must be between 2 and 120 characters.');
      setCreateMessage(null);
      return;
    }
    if (!summary) {
      setCreateError('Summary is required.');
      setCreateMessage(null);
      return;
    }
    if (summary.length < 2 || summary.length > 500) {
      setCreateError('Summary must be between 2 and 500 characters.');
      setCreateMessage(null);
      return;
    }
    if (!description) {
      setCreateError('Description is required.');
      setCreateMessage(null);
      return;
    }
    if (description.length < 2 || description.length > 10000) {
      setCreateError('Description must be between 2 and 10000 characters.');
      setCreateMessage(null);
      return;
    }

    setCreateError(null);
    setCreateMessage(null);

    postCareerJob.mutate(
      {
        title,
        requisitionId,
        team,
        brainNodeId,
        location,
        workType: createForm.workType,
        summary,
        description,
        responsibilities: splitMultilineList(createForm.responsibilities),
        requirements: splitMultilineList(createForm.requirements),
        hiringManager: createForm.hiringManager.trim(),
        targetHireCount: normalizeTargetHireCount(createForm.targetHireCount, 1),
        published: createForm.published,
        listingStatus: createForm.published ? 'published' : 'draft',
      },
      {
        onSuccess: (result) => {
          setCreateMessage(result.remoteSynced ? 'Job listing created and synced.' : 'Job listing created.');
          queryClient.invalidateQueries({ queryKey: ['job-board'] });
          setCreateForm(DEFAULT_CREATE_LISTING_FORM);
        },
        onError: (error) => {
          setCreateError(error instanceof Error ? error.message : 'Failed to create listing.');
        },
      },
    );
  };

  const applyListingStatus = (job: CareerJob, nextStatus: JobListingStatus) => {
    setListingStatus.mutate(
      {
        jobId: job.id,
        status: nextStatus,
      },
      {
        onSuccess: () => {
          setDetailsError(null);
          setDetailsMessage(`Listing moved to ${formatListingStatus(nextStatus)}.`);
        },
        onError: (error) => {
          setDetailsMessage(null);
          setDetailsError(
            error instanceof Error ? error.message : 'Failed to update listing status.',
          );
        },
      },
    );
  };

  const runListingAction = (job: CareerJob, action: ListingActionKey) => {
    setOpenListingActionsId(null);
    if (action === 'pause') {
      applyListingStatus(job, 'paused');
      return;
    }
    if (action === 'reactivate') {
      applyListingStatus(job, 'published');
      return;
    }
    if (action === 'delete') {
      applyListingStatus(job, 'deleted');
      return;
    }
    if (action === 'filled') {
      applyListingStatus(job, 'closed_filled');
      return;
    }
    if (action === 'publish') {
      applyListingStatus(job, 'published');
      return;
    }
    applyListingStatus(job, 'draft');
  };

  const saveListingDetails = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedJob || !listingForm) return;

    if (!listingForm.title.trim() || !listingForm.requisitionId.trim()) {
      setDetailsError('Title and requisition number are required.');
      setDetailsMessage(null);
      return;
    }
    if (!listingForm.brainNodeId.trim()) {
      setDetailsError('Select the Brain team node responsible for this role.');
      setDetailsMessage(null);
      return;
    }

    const team =
      brainTeamOptionById.get(listingForm.brainNodeId.trim())?.teamLabel ??
      listingForm.team.trim();

    updateListing.mutate(
      {
        jobId: selectedJob.id,
        patch: {
          title: listingForm.title.trim(),
          requisitionId: listingForm.requisitionId.trim(),
          team,
          brainNodeId: listingForm.brainNodeId.trim(),
          location: listingForm.location.trim(),
          workType: listingForm.workType,
          summary: listingForm.summary.trim(),
          description: listingForm.description.trim(),
          responsibilities: splitMultilineList(listingForm.responsibilities),
          requirements: splitMultilineList(listingForm.requirements),
        },
      },
      {
        onSuccess: () => {
          setDetailsError(null);
          setDetailsMessage('Listing details saved.');
        },
        onError: (error) => {
          setDetailsMessage(null);
          setDetailsError(
            error instanceof Error ? error.message : 'Failed to save listing details.',
          );
        },
      },
    );
  };

  const saveOverviewDetails = () => {
    if (!selectedJob || !overviewForm) return;

    updateListing.mutate(
      {
        jobId: selectedJob.id,
        patch: {
          hiringManager: overviewForm.hiringManager.trim(),
          targetHireCount: normalizeTargetHireCount(overviewForm.targetHireCount, 1),
          compensation: overviewForm.compensation.trim(),
        },
      },
      {
        onSuccess: () => {
          setDetailsError(null);
          setDetailsMessage('Overview details saved.');
        },
        onError: (error) => {
          setDetailsMessage(null);
          setDetailsError(
            error instanceof Error ? error.message : 'Failed to save overview details.',
          );
        },
      },
    );
  };

  const addPhase = () => {
    if (!selectedJob) return;
    const nextOrder = phaseDrafts.length + 1;
    const stageName = (LIFECYCLE_STAGE_LIBRARY[0] ?? 'Phone screen').trim();

    setPhaseDrafts((prev) => [
      ...prev,
      {
        id: `phase_${stageIdSuffix(stageName)}_${stageIdSuffix(selectedJob.id)}_${nextOrder}`,
        name: stageName,
        order: nextOrder,
        interviewer: '',
        notes: '',
        scorecard: null,
      },
    ]);
  };

  const movePhaseDraft = (phaseId: string, direction: 'up' | 'down') => {
    setPhaseDrafts((prev) => {
      const index = prev.findIndex((phase) => phase.id === phaseId);
      if (index < 1) return prev;
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      // Keep Stage 1 fixed as review application.
      if (targetIndex < 1 || targetIndex >= prev.length) return prev;

      const next = [...prev];
      const current = next[index];
      next[index] = next[targetIndex];
      next[targetIndex] = current;
      return next.map((phase, orderIndex) => ({ ...phase, order: orderIndex + 1 }));
    });
  };

  const updatePhaseDraft = (
    phaseId: string,
    patch: Partial<Pick<JobApplicationPhase, 'name' | 'interviewer' | 'notes'>>,
  ) => {
    setPhaseDrafts((prev) =>
      prev.map((phase, index) => {
        if (phase.id !== phaseId) return phase;
        const nextName =
          index === 0
            ? DEFAULT_REVIEW_STAGE_NAME
            : (patch.name ?? phase.name).trim() || phase.name;
        return {
          ...phase,
          ...patch,
          name: nextName,
        };
      }),
    );
  };

  const addPhaseInterviewer = (phaseId: string, interviewerValue: string) => {
    const normalized = interviewerValue.trim();
    if (!normalized) return;

    setPhaseDrafts((prev) =>
      prev.map((phase) => {
        if (phase.id !== phaseId) return phase;
        const existing = parseInterviewerList(phase.interviewer);
        return {
          ...phase,
          interviewer: joinInterviewerList([...existing, normalized]),
        };
      }),
    );
  };

  const removePhaseInterviewer = (phaseId: string, interviewerValue: string) => {
    const normalizedKey = interviewerValue.trim().toLowerCase();
    if (!normalizedKey) return;

    setPhaseDrafts((prev) =>
      prev.map((phase) => {
        if (phase.id !== phaseId) return phase;
        const remaining = parseInterviewerList(phase.interviewer).filter(
          (value) => value.trim().toLowerCase() !== normalizedKey,
        );
        return {
          ...phase,
          interviewer: joinInterviewerList(remaining),
        };
      }),
    );
  };

  const removePhaseDraft = (phaseId: string) => {
    setPhaseDrafts((prev) => {
      if (prev.length <= 1) return prev;
      return prev
        .filter((phase, index) => index === 0 || phase.id !== phaseId)
        .map((phase, index) => ({ ...phase, order: index + 1 }));
    });
  };

  const saveLifecycleStages = () => {
    if (!selectedJob) return;
    const normalized = normalizePhaseDrafts(phaseDrafts, selectedJob.id);

    saveListing.mutate(
      {
        job: {
          ...selectedJob,
          applicationPhases: normalized,
        },
      },
      {
        onSuccess: () => {
          setDetailsError(null);
          setDetailsMessage('Lifecycle stages saved.');
          setPhaseDrafts(normalized);
        },
        onError: (error) => {
          setDetailsMessage(null);
          setDetailsError(
            error instanceof Error ? error.message : 'Failed to save lifecycle stages.',
          );
        },
      },
    );
  };

  const saveCandidateLifecycle = (
    candidate: CandidateApplicationRecord,
    draftOverride?: CandidateLifecycleDraft,
    successMessage = 'Candidate lifecycle updated.',
    options?: { offerAccepted?: boolean },
  ) => {
    const draft = draftOverride ?? candidateDrafts[candidate.id];
    if (!draft) return;
    const interviewersByPhase = phaseInterviewersById(selectedPhases);
    const currentPhaseInterviewers =
      interviewersByPhase[draft.phaseId] ?? parseInterviewerList(draft.reviewer);
    const currentPhaseScores = draft.stageReviewerScores[draft.phaseId] ?? {};
    const stageAverage = stageScoreAverage(currentPhaseScores, currentPhaseInterviewers);
    const parsedDraftScore = draft.scorecard.trim()
      ? Number.parseInt(parseScoreInput(draft.scorecard), 10)
      : null;
    const parsedScore =
      currentPhaseInterviewers.length > 0
        ? stageAverage !== null
          ? Math.round(stageAverage)
          : null
        : parsedDraftScore !== null && Number.isFinite(parsedDraftScore)
        ? parsedDraftScore
        : null;
    const reviewer = joinInterviewerList(currentPhaseInterviewers);
    const stageReviewerScoresPayload = Object.entries(draft.stageReviewerScores).reduce<
      Record<string, Record<string, number>>
    >((acc, [phaseId, reviewerScores]) => {
      if (!phaseId.trim() || !reviewerScores || typeof reviewerScores !== 'object') {
        return acc;
      }
      const normalizedScores: Record<string, number> = {};
      for (const [reviewerEmail, scoreValue] of Object.entries(reviewerScores)) {
        const reviewerKey = reviewerEmail.trim();
        if (!reviewerKey) continue;
        const parsed = Number.parseInt(parseScoreInput(scoreValue ?? ''), 10);
        if (!Number.isFinite(parsed)) continue;
        normalizedScores[reviewerKey] = parsed;
      }
      if (Object.keys(normalizedScores).length > 0) {
        acc[phaseId] = normalizedScores;
      }
      return acc;
    }, {});
    setCandidateSavingId(candidate.id);
    updateCandidateLifecycle.mutate(
      {
        candidateId: candidate.id,
        status: draft.status,
        phaseId: draft.phaseId,
        reviewer,
        notes: draft.notes,
        scorecard: parsedScore !== null && Number.isFinite(parsedScore) ? parsedScore : null,
        stageReviewerScores: stageReviewerScoresPayload,
        offerAccepted: options?.offerAccepted,
      },
      {
        onSuccess: () => {
          setDetailsError(null);
          setDetailsMessage(successMessage);
          setCandidateSavingId(null);
        },
        onError: (error) => {
          setDetailsMessage(null);
          setDetailsError(
            error instanceof Error ? error.message : 'Failed to update candidate lifecycle.',
          );
          setCandidateSavingId(null);
        },
      },
    );
  };

  const applyCandidateDecision = (
    candidate: CandidateApplicationRecord,
    decision: 'continue' | 'reject',
  ) => {
    const draft = candidateDrafts[candidate.id];
    if (!draft || selectedPhases.length === 0) return;

    const currentIndex = resolvePhaseIndex(selectedPhases, draft.phaseId);
    if (currentIndex < 0) return;

    const currentPhaseId = selectedPhases[currentIndex]?.id ?? '';
    if (!currentPhaseId) return;
    const interviewersByPhase = phaseInterviewersById(selectedPhases);
    const currentPhase = selectedPhases[currentIndex];
    const currentStageInterviewers = interviewersByPhase[currentPhaseId] ?? [];
    const currentStageScores = draft.stageReviewerScores[currentPhaseId] ?? {};
    const currentStageSubmittedCount = stageScoreSubmissionCount(
      currentStageScores,
      currentStageInterviewers,
    );

    const nextDraft: CandidateLifecycleDraft = {
      ...draft,
      phaseId: currentPhaseId,
    };
    let successMessage = 'Candidate lifecycle updated.';

    if (decision === 'reject') {
      nextDraft.status = 'rejected';
      successMessage = `Candidate rejected at Stage ${currentIndex + 1}.`;
    } else if (currentIndex < selectedPhases.length - 1) {
      if (
        currentStageInterviewers.length > 0 &&
        currentStageSubmittedCount < currentStageInterviewers.length
      ) {
        setDetailsMessage(null);
        setDetailsError(
          `All assigned interviewers for Stage ${currentPhase.order} (${currentPhase.name}) must submit a score before continuing (${currentStageSubmittedCount}/${currentStageInterviewers.length} submitted).`,
        );
        return;
      }
      const nextPhase = selectedPhases[currentIndex + 1];
      const nextStageInterviewers = interviewersByPhase[nextPhase.id] ?? [];
      if (nextStageInterviewers.length === 0) {
        setDetailsMessage(null);
        setDetailsError(
          `Assign at least one interviewer to Stage ${nextPhase.order} (${nextPhase.name}) before advancing this candidate.`,
        );
        return;
      }
      nextDraft.phaseId = nextPhase.id;
      nextDraft.status = 'in_review';
      successMessage = `Candidate advanced to Stage ${nextPhase.order}.`;
    } else {
      if (
        currentStageInterviewers.length > 0 &&
        currentStageSubmittedCount < currentStageInterviewers.length
      ) {
        setDetailsMessage(null);
        setDetailsError(
          `All assigned interviewers for Stage ${currentPhase.order} (${currentPhase.name}) must submit a score before marking this candidate hired (${currentStageSubmittedCount}/${currentStageInterviewers.length} submitted).`,
        );
        return;
      }
      nextDraft.status = 'hired';
      successMessage = 'Candidate marked hired.';
    }

    setCandidateDrafts((prev) => ({
      ...prev,
      [candidate.id]: nextDraft,
    }));
    saveCandidateLifecycle(candidate, nextDraft, successMessage);
  };

  const acceptCandidateOffer = (candidate: CandidateApplicationRecord) => {
    const draft = candidateDrafts[candidate.id];
    if (!draft) return;
    const nextDraft: CandidateLifecycleDraft = {
      ...draft,
      status: 'hired',
    };
    setCandidateDrafts((prev) => ({
      ...prev,
      [candidate.id]: nextDraft,
    }));
    saveCandidateLifecycle(
      candidate,
      nextDraft,
      'Offer accepted. Candidate has been converted to agent and onboarding has started.',
      { offerAccepted: true },
    );
  };

  const listingCandidateStats = (job: CareerJob) => {
    const rows = candidates.filter(
      (candidate) =>
        candidate.jobId === job.id ||
        candidate.requisitionId.toLowerCase() === job.requisitionId.toLowerCase(),
    );
    const received = rows.filter(
      (candidate) =>
        normalizeCandidateApplicationStatus(candidate.status) === 'application_received',
    ).length;
    const inReview = rows.filter(
      (candidate) => normalizeCandidateApplicationStatus(candidate.status) === 'in_review',
    ).length;
    const rejected = rows.filter(
      (candidate) => normalizeCandidateApplicationStatus(candidate.status) === 'rejected',
    ).length;
    const hired = rows.filter(
      (candidate) => normalizeCandidateApplicationStatus(candidate.status) === 'hired',
    ).length;
    const notScheduled = rows.filter(
      (candidate) => normalizeInterviewLifecycleStatus(candidate.interviewStatus) === 'not_scheduled',
    ).length;
    const scheduled = rows.filter(
      (candidate) => normalizeInterviewLifecycleStatus(candidate.interviewStatus) === 'scheduled',
    ).length;
    const inProgress = rows.filter(
      (candidate) => normalizeInterviewLifecycleStatus(candidate.interviewStatus) === 'in_progress',
    ).length;
    const complete = rows.filter(
      (candidate) => normalizeInterviewLifecycleStatus(candidate.interviewStatus) === 'complete',
    ).length;
    return {
      total: rows.length,
      received,
      inReview,
      rejected,
      hired,
      notScheduled,
      scheduled,
      inProgress,
      complete,
    };
  };

  const selectedListingStats = selectedJob ? listingCandidateStats(selectedJob) : null;
  const overviewHiringManager = (overviewForm?.hiringManager ?? selectedJob?.hiringManager ?? '').trim();
  const overviewHiringManagerOption = overviewHiringManager
    ? interviewerDirectory.get(overviewHiringManager.toLowerCase()) ?? null
    : null;
  const overviewHiringManagerLabel =
    overviewHiringManagerOption?.name || overviewHiringManager || 'Unassigned';
  const overviewTargetHireCount = normalizeTargetHireCount(
    overviewForm?.targetHireCount ?? selectedJob?.targetHireCount,
    1,
  );
  const overviewOpenSeats = Math.max(
    0,
    overviewTargetHireCount - (selectedListingStats?.hired ?? 0),
  );
  const overviewCompensation = (overviewForm?.compensation ?? selectedJob?.compensation ?? '').trim();
  const hiringManagerOptions = useMemo(
    () => [
      { value: '', label: 'Unassigned' },
      ...interviewerStaffOptions.map((option) => ({
        value: option.value,
        label: `${option.name} - ${option.roleLabel}`,
      })),
    ],
    [interviewerStaffOptions],
  );

  return (
    <>
      <Wrap>
        <Top>
          <Heading>Job Board</Heading>
          <Subtitle>
            Create and manage listings, track candidates by role, and run hiring lifecycle stages with scorecards.
          </Subtitle>
        </Top>

        <StatsGrid>
          <SmallMetricGreen>
            <MetricNumber>{stats.totalListings}</MetricNumber>
            <MetricLabel>Total listings</MetricLabel>
          </SmallMetricGreen>
          <SmallMetricGray>
            <MetricNumberRed>{stats.active}</MetricNumberRed>
            <MetricLabel>Published</MetricLabel>
          </SmallMetricGray>
          <SmallMetricGreen>
            <MetricNumber>{stats.paused}</MetricNumber>
            <MetricLabel>Paused</MetricLabel>
          </SmallMetricGreen>
          <SmallMetricGray>
            <MetricNumberRed>{stats.closed}</MetricNumberRed>
            <MetricLabel>Closed - filled</MetricLabel>
          </SmallMetricGray>
          <SmallMetricGreen>
            <MetricNumber>{stats.totalCandidates}</MetricNumber>
            <MetricLabel>Candidates</MetricLabel>
          </SmallMetricGreen>
          <SmallMetricGray>
            <MetricNumberRed>{stats.hired}</MetricNumberRed>
            <MetricLabel>Hired</MetricLabel>
          </SmallMetricGray>
        </StatsGrid>

        <FilterPanel>
          <FilterRow>
            <SearchInput
              value={q}
              onChange={(event) => {
                setQ(event.target.value);
                setPage(0);
              }}
              placeholder="Search by title, requisition, team, or location..."
            />
            <GlassPicklist
              value={statusFilter}
              options={LISTING_STATUS_FILTER_OPTIONS}
              ariaLabel="Filter job listings by status"
              onChange={(nextStatus) => {
                setStatusFilter(nextStatus);
                setPage(0);
              }}
            />
            <PrimaryButton
              type="button"
              onClick={() => {
                setIsCreateModalOpen(true);
                setCreateError(null);
                setCreateMessage(null);
              }}
            >
              Create Listing
            </PrimaryButton>
          </FilterRow>
        </FilterPanel>

        <JobBoardListSection>
          <JobBoardListViewport>
            {boardQuery.isLoading ? (
              <LoadingPanel>Loading job board...</LoadingPanel>
            ) : (
              <AccordionList>
                {paginatedListings.map((job) => {
                  const status = getCareerJobListingStatus(job);
                  const listingActions = getListingToggleActions(status);
                  const listingPhases = getCareerJobApplicationPhases(job);
                  const phaseCount = listingPhases.length;
                  const statsRow = listingCandidateStats(job);
                  const expanded = expandedListingId === job.id;

                  return (
                    <ListingItem key={job.id} $menuOpen={openListingActionsId === job.id}>
                      <ListingHeader>
                        <ListingIdentity
                          type="button"
                          onClick={() => {
                            setOpenListingActionsId(null);
                            setExpandedListingId((current) => (current === job.id ? null : job.id));
                          }}
                        >
                          <ListingTitle>{job.title}</ListingTitle>
                          <ListingMeta>
                            {job.requisitionId} • {job.team} • {job.location} • {formatWorkType(job.workType)}
                          </ListingMeta>
                        </ListingIdentity>

                        <HeaderRight>
                          <Pill $tone="gray">{statsRow.total} candidates</Pill>
                          <ActionMenuShell>
                            <ActionMenuButton
                              type="button"
                              aria-label={`Open actions for ${job.title}`}
                              aria-haspopup="menu"
                              aria-expanded={openListingActionsId === job.id}
                              onClick={() =>
                                setOpenListingActionsId((current) =>
                                  current === job.id ? null : job.id,
                                )
                              }
                            >
                              ...
                            </ActionMenuButton>
                            {openListingActionsId === job.id ? (
                              <ActionMenuPanel role="menu" aria-label={`Actions for ${job.title}`}>
                                {listingActions.map((action) => (
                                  <ActionMenuItem
                                    key={`${job.id}-${action.id}`}
                                    type="button"
                                    $danger={action.danger}
                                    onClick={() => runListingAction(job, action.key)}
                                  >
                                    {action.label}
                                  </ActionMenuItem>
                                ))}
                              </ActionMenuPanel>
                            ) : null}
                          </ActionMenuShell>
                        </HeaderRight>
                      </ListingHeader>

                      {expanded ? (
                        <ListingBody>
                          <BodySummary>{job.summary}</BodySummary>

                          <LifecycleTimeline>
                            <TimelineTrack>
                              {listingPhases.map((phase, index) => {
                                const stageInterviewers = parseInterviewerList(phase.interviewer);
                                return (
                                  <TimelineStep key={phase.id}>
                                    <TimelineNode>
                                      <TimelineNodeHeader>
                                        <TimelineStageNumber>{phase.order}</TimelineStageNumber>
                                        <TimelineStageName>{phase.name}</TimelineStageName>
                                      </TimelineNodeHeader>
                                      <TimelineInterviewer>
                                        {stageInterviewers.length === 0
                                          ? 'No interviewer assigned'
                                          : stageInterviewers.length === 1
                                          ? stageInterviewers[0]
                                          : `${stageInterviewers.length} interviewers assigned`}
                                      </TimelineInterviewer>
                                    </TimelineNode>
                                    {index < listingPhases.length - 1 ? <TimelineConnector /> : null}
                                  </TimelineStep>
                                );
                              })}
                            </TimelineTrack>
                          </LifecycleTimeline>

                          <FooterActions>
                            <PipelineRow>
                              <StageChip>Phases: {phaseCount}</StageChip>
                              <StageChip>Received: {statsRow.received}</StageChip>
                              <StageChip>In review: {statsRow.inReview}</StageChip>
                              <StageChip>Rejected: {statsRow.rejected}</StageChip>
                              <StageChip>Hired: {statsRow.hired}</StageChip>
                            </PipelineRow>
                            <DetailsButton
                              type="button"
                              onClick={() => {
                                setOpenListingActionsId(null);
                                setDetailsListingId(job.id);
                                setDetailsTab('overview');
                              }}
                            >
                              Open Details
                            </DetailsButton>
                          </FooterActions>
                        </ListingBody>
                      ) : null}
                    </ListingItem>
                  );
                })}

                {!filteredListings.length ? (
                  <EmptyState>No listings match your current filters.</EmptyState>
                ) : null}
              </AccordionList>
            )}
          </JobBoardListViewport>

          <PaginationPanel>
            <PageInfo>
              Showing page {page + 1} of {totalPages} ({filteredListings.length} listings)
            </PageInfo>
            <PagerGroup>
              <PagerButton
                type="button"
                disabled={boardQuery.isLoading || page === 0}
                onClick={() => setPage((current) => Math.max(0, current - 1))}
              >
                Previous
              </PagerButton>
              <PagerButton
                type="button"
                disabled={boardQuery.isLoading || page >= totalPages - 1}
                onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
              >
                Next
              </PagerButton>
            </PagerGroup>
            <PagePill>Page {page + 1}</PagePill>
          </PaginationPanel>
        </JobBoardListSection>
      </Wrap>

      <DrawerBackdrop
        type="button"
        aria-label="Close job details"
        $open={Boolean(selectedJob)}
        onClick={closeDetails}
      />
      <Drawer $open={Boolean(selectedJob)}>
        {selectedJob ? (
          <>
            <DrawerHeader>
              <DrawerHeaderBar>
                <DrawerTitleWrap>
                  <DrawerEyebrow>Job details</DrawerEyebrow>
                  <DrawerTitle>{selectedJob.title}</DrawerTitle>
                  <DrawerSub>
                    {selectedJob.requisitionId} - {selectedCandidates.length} candidates -{' '}
                    {formatListingStatus(getCareerJobListingStatus(selectedJob))}
                  </DrawerSub>
                </DrawerTitleWrap>
                <DrawerCloseButton type="button" onClick={closeDetails} aria-label="Close job details">
                  x
                </DrawerCloseButton>
              </DrawerHeaderBar>
            </DrawerHeader>

            <Tabs>
              <Tab type="button" $active={detailsTab === 'overview'} onClick={() => setDetailsTab('overview')}>
                Overview
              </Tab>
              <Tab type="button" $active={detailsTab === 'listing'} onClick={() => setDetailsTab('listing')}>
                Listing
              </Tab>
              <Tab type="button" $active={detailsTab === 'candidates'} onClick={() => setDetailsTab('candidates')}>
                Candidates
              </Tab>
              <Tab type="button" $active={detailsTab === 'lifecycle'} onClick={() => setDetailsTab('lifecycle')}>
                Lifecycle
              </Tab>
            </Tabs>

            <DrawerBody>
              {detailsError ? <Message $error>{detailsError}</Message> : null}
              {!detailsError && detailsMessage ? <Message>{detailsMessage}</Message> : null}

              {detailsTab === 'overview' ? (
                <Panel>
                  <PanelTitle>Overview</PanelTitle>
                  <FooterMeta>
                    {selectedJob.requisitionId} - {selectedJob.team} - {selectedJob.location} -{' '}
                    {formatWorkType(selectedJob.workType)}
                  </FooterMeta>
                  <OverviewSummary>{selectedJob.summary}</OverviewSummary>
                  <OverviewGrid>
                    <OverviewCard>
                      <OverviewLabel>Listing status</OverviewLabel>
                      <OverviewValue>
                        {formatListingStatus(getCareerJobListingStatus(selectedJob))}
                      </OverviewValue>
                      <OverviewSub>{selectedPhases.length} lifecycle stages configured</OverviewSub>
                    </OverviewCard>
                    <OverviewCard>
                      <OverviewLabel>Total candidates</OverviewLabel>
                      <OverviewValue>{selectedListingStats?.total ?? 0}</OverviewValue>
                      <OverviewSub>{selectedListingStats?.inReview ?? 0} currently in review</OverviewSub>
                    </OverviewCard>
                    <OverviewCard>
                      <OverviewLabel>Hiring manager</OverviewLabel>
                      <GlassPicklist
                        value={overviewForm?.hiringManager ?? ''}
                        options={hiringManagerOptions}
                        ariaLabel="Select hiring manager"
                        onChange={(nextHiringManager) =>
                          setOverviewForm((prev) => ({
                            hiringManager: nextHiringManager,
                            targetHireCount: prev?.targetHireCount ?? overviewTargetHireCount,
                            compensation: prev?.compensation ?? overviewCompensation,
                          }))
                        }
                      />
                      <OverviewSub>{overviewHiringManagerLabel}</OverviewSub>
                    </OverviewCard>
                    <OverviewCard>
                      <OverviewLabel>Target hires</OverviewLabel>
                      <OverviewFieldInput
                        type="number"
                        min={1}
                        max={99}
                        value={overviewForm?.targetHireCount ?? overviewTargetHireCount}
                        onChange={(event) =>
                          setOverviewForm((prev) => ({
                            hiringManager: prev?.hiringManager ?? overviewHiringManager,
                            targetHireCount: normalizeTargetHireCount(
                              Number(event.target.value),
                              overviewTargetHireCount,
                            ),
                            compensation: prev?.compensation ?? overviewCompensation,
                          }))
                        }
                      />
                      <OverviewSub>{overviewOpenSeats} open seat(s) remaining</OverviewSub>
                    </OverviewCard>
                    <OverviewCard>
                      <OverviewLabel>Compensation</OverviewLabel>
                      <OverviewFieldInput
                        type="text"
                        value={overviewForm?.compensation ?? overviewCompensation}
                        onChange={(event) =>
                          setOverviewForm((prev) => ({
                            hiringManager: prev?.hiringManager ?? overviewHiringManager,
                            targetHireCount: prev?.targetHireCount ?? overviewTargetHireCount,
                            compensation: event.target.value,
                          }))
                        }
                        placeholder="Example: $120,000 - $145,000"
                      />
                      <OverviewSub>Internal compensation note for this role</OverviewSub>
                    </OverviewCard>
                    <OverviewCard>
                      <OverviewLabel>Applications received</OverviewLabel>
                      <OverviewValue>{selectedListingStats?.received ?? 0}</OverviewValue>
                      <OverviewSub>New applications awaiting progression</OverviewSub>
                    </OverviewCard>
                    <OverviewCard>
                      <OverviewLabel>Decision outcomes</OverviewLabel>
                      <OverviewValue>
                        {selectedListingStats?.hired ?? 0} hired /{' '}
                        {selectedListingStats?.rejected ?? 0} rejected
                      </OverviewValue>
                      <OverviewSub>Final decisions for this listing</OverviewSub>
                    </OverviewCard>
                  </OverviewGrid>
                  <InlineActions>
                    <SecondaryButton
                      type="button"
                      onClick={saveOverviewDetails}
                      disabled={!overviewForm || updateListing.isPending}
                    >
                      {updateListing.isPending ? 'Saving...' : 'Save overview'}
                    </SecondaryButton>
                  </InlineActions>
                </Panel>
              ) : null}

              {detailsTab === 'listing' && listingForm ? (
                <Panel>
                  <PanelTitle>Listing details</PanelTitle>
                  <form onSubmit={saveListingDetails}>
                    <InputGrid>
                      <Field>
                        Job title
                        <Input
                          value={listingForm.title}
                          onChange={(event) =>
                            setListingForm((prev) =>
                              prev ? { ...prev, title: event.target.value } : prev,
                            )
                          }
                        />
                      </Field>
                      <Field>
                        Requisition number
                        <Input
                          value={listingForm.requisitionId}
                          onChange={(event) =>
                            setListingForm((prev) =>
                              prev ? { ...prev, requisitionId: event.target.value } : prev,
                            )
                          }
                        />
                      </Field>
                      <Field>
                        Brain team node
                        <Input
                          as="select"
                          value={listingForm.brainNodeId}
                          onChange={(event) =>
                            setListingForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    brainNodeId: event.target.value,
                                    team:
                                      brainTeamOptionById.get(event.target.value)?.teamLabel ?? '',
                                  }
                                : prev,
                            )
                          }
                          disabled={brainTeamOptionsLoading}
                        >
                          <option value="">
                            {brainTeamOptionsLoading
                              ? 'Loading team nodes...'
                              : brainTeamOptions.length
                                ? 'Select Brain team node'
                                : 'No active team nodes found'}
                          </option>
                          {brainTeamOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </Field>
                      <Field>
                        Team
                        <Input
                          value={listingForm.team}
                          readOnly
                          placeholder="Select a Brain team node"
                        />
                      </Field>
                      <Field>
                        Location
                        <Input
                          value={listingForm.location}
                          onChange={(event) =>
                            setListingForm((prev) =>
                              prev ? { ...prev, location: event.target.value } : prev,
                            )
                          }
                        />
                      </Field>
                      <Field>
                        Work type
                        <GlassPicklist
                          value={listingForm.workType}
                          options={CREATE_LISTING_WORK_TYPE_OPTIONS}
                          ariaLabel="Select work type"
                          onChange={(workType) =>
                            setListingForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    workType,
                                  }
                                : prev,
                            )
                          }
                        />
                      </Field>
                    </InputGrid>

                    <Field>
                      Summary
                      <Textarea
                        value={listingForm.summary}
                        onChange={(event) =>
                          setListingForm((prev) =>
                            prev ? { ...prev, summary: event.target.value } : prev,
                          )
                        }
                      />
                    </Field>

                    <Field>
                      Description
                      <Textarea
                        value={listingForm.description}
                        onChange={(event) =>
                          setListingForm((prev) =>
                            prev ? { ...prev, description: event.target.value } : prev,
                          )
                        }
                      />
                    </Field>

                    <InputGrid>
                      <Field>
                        Responsibilities (one per line)
                        <Textarea
                          value={listingForm.responsibilities}
                          onChange={(event) =>
                            setListingForm((prev) =>
                              prev ? { ...prev, responsibilities: event.target.value } : prev,
                            )
                          }
                        />
                      </Field>
                      <Field>
                        Requirements (one per line)
                        <Textarea
                          value={listingForm.requirements}
                          onChange={(event) =>
                            setListingForm((prev) =>
                              prev ? { ...prev, requirements: event.target.value } : prev,
                            )
                          }
                        />
                      </Field>
                    </InputGrid>

                    <InlineActions>
                      <SecondaryButton type="submit">
                        {updateListing.isPending ? 'Saving...' : 'Save listing'}
                      </SecondaryButton>
                    </InlineActions>
                  </form>
                </Panel>
              ) : null}

              {detailsTab === 'candidates' ? (
                <Panel>
                  <PanelTitle>Candidate lifecycle</PanelTitle>
                  {selectedCandidates.length === 0 ? (
                    <EmptyState>No candidates have applied to this listing yet.</EmptyState>
                  ) : (
                    selectedCandidates.map((candidate) => {
                      const draft =
                        candidateDrafts[candidate.id] ??
                        candidateDraftFromRecord(candidate, selectedPhases);
                      const isExpanded = expandedCandidateId === candidate.id;
                      const currentIndex = resolvePhaseIndex(selectedPhases, draft.phaseId);
                      const currentPhase = selectedPhases[currentIndex] ?? null;
                      const isRejected = draft.status === 'rejected';
                      const isHired = draft.status === 'hired';
                      const hasAcceptedOffer = Boolean(candidate.offerAccepted || candidate.agentUserId);
                      const nextPhase =
                        currentIndex >= 0 && currentIndex < selectedPhases.length - 1
                          ? selectedPhases[currentIndex + 1]
                          : null;
                      const continueLabel = isHired
                        ? hasAcceptedOffer
                          ? 'Offer accepted'
                          : 'Candidate accept offer letter?'
                        : nextPhase
                        ? `Continue to Stage ${nextPhase.order}`
                        : selectedPhases.length > 0
                        ? 'Mark hired'
                        : 'Continue';
                      const interviewersByPhase = phaseInterviewersById(selectedPhases);
                      const currentStageInterviewers = currentPhase
                        ? interviewersByPhase[currentPhase.id] ?? []
                        : [];
                      const currentStageScores = currentPhase
                        ? draft.stageReviewerScores[currentPhase.id] ?? {}
                        : {};
                      const currentStageSubmittedCount = stageScoreSubmissionCount(
                        currentStageScores,
                        currentStageInterviewers,
                      );
                      const currentStageAverage = stageScoreAverage(
                        currentStageScores,
                        currentStageInterviewers,
                      );
                      const timelineTotalScore = selectedPhases.reduce((sum, phase) => {
                        const stageInterviewers = interviewersByPhase[phase.id] ?? [];
                        const stageScores = draft.stageReviewerScores[phase.id] ?? {};
                        const stageAverage = stageScoreAverage(stageScores, stageInterviewers);
                        return sum + (stageAverage ?? 0);
                      }, 0);
                      const timelineTotalMax = selectedPhases.length * 10;
                      const applicantPhone = candidate.contactPhone.trim() || 'Not provided';
                      const applicantAddressLine = candidate.businessAddress.trim();
                      const applicantRegionParts = [
                        candidate.candidateCity.trim(),
                        candidate.candidateState.trim(),
                      ].filter(Boolean);
                      const applicantPostalCode = candidate.candidatePostalCode.trim();
                      const applicantRegion = applicantRegionParts.join(', ');
                      const applicantAddress = [applicantAddressLine, applicantRegion, applicantPostalCode]
                        .filter(Boolean)
                        .join(' ')
                        .trim() || 'Not provided';
                      const applicantReferredBy = candidate.pointOfContact.trim() || 'Not provided';
                      const backgroundCheckStatus = candidate.backgroundCheckAcknowledged
                        ? 'Acknowledged'
                        : 'Not acknowledged';
                      const linkedInUrl = normalizeExternalLink(candidate.linkedInUrl);
                      const portfolioUrl = normalizeExternalLink(candidate.portfolioUrl);
                      return (
                        <CandidateCard key={candidate.id}>
                          <CandidateHeader>
                            <CandidateHeaderButton
                              type="button"
                              onClick={() =>
                                setExpandedCandidateId((prev) =>
                                  prev === candidate.id ? null : candidate.id,
                                )
                              }
                              aria-expanded={isExpanded}
                            >
                              <CandidateIdentity>
                                <CandidateAvatar>{initials(candidate.name || candidate.email)}</CandidateAvatar>
                                <div>
                                  <CandidateName>{candidate.name || candidate.email}</CandidateName>
                                  <CandidateEmail>{candidate.email}</CandidateEmail>
                                </div>
                              </CandidateIdentity>
                              <CandidateHeaderRight>
                                <CandidateStatusPill
                                  $tone={
                                    draft.status === 'application_received'
                                      ? 'yellow'
                                      : draft.status === 'in_review'
                                      ? 'green'
                                      : draft.status === 'hired'
                                      ? 'green'
                                      : 'red'
                                  }
                                >
                                  {candidateStatusOptions.find((item) => item.value === draft.status)?.label}
                                </CandidateStatusPill>
                                <CandidateChevron $expanded={isExpanded}>v</CandidateChevron>
                              </CandidateHeaderRight>
                            </CandidateHeaderButton>
                          </CandidateHeader>

                          {isExpanded ? (
                            <CandidateBody>
                              <CandidateTopActionsRow>
                                {isHired ? (
                                  <CandidateHiredChip>Hired</CandidateHiredChip>
                                ) : (
                                  <CandidateRejectButton
                                    type="button"
                                    onClick={() => applyCandidateDecision(candidate, 'reject')}
                                    disabled={
                                      selectedPhases.length === 0 ||
                                      isRejected ||
                                      candidateSavingId === candidate.id
                                    }
                                  >
                                    Reject
                                  </CandidateRejectButton>
                                )}
                              </CandidateTopActionsRow>
                              {selectedPhases.length > 0 ? (
                                <CandidateStageTimeline>
                                  <CandidateStageTrack>
                                    {selectedPhases.map((phase, index) => {
                                      let state: 'completed' | 'current' | 'remaining' | 'rejected' =
                                        'remaining';
                                      const phaseInterviewers =
                                        interviewersByPhase[phase.id] ?? [];
                                      const phaseScores = draft.stageReviewerScores[phase.id] ?? {};
                                      const phaseSubmittedCount = stageScoreSubmissionCount(
                                        phaseScores,
                                        phaseInterviewers,
                                      );
                                      const phaseAverage = stageScoreAverage(
                                        phaseScores,
                                        phaseInterviewers,
                                      );

                                      if (isHired || index < currentIndex) {
                                        state = 'completed';
                                      } else if (isRejected && index === currentIndex) {
                                        state = 'rejected';
                                      } else if (!isRejected && !isHired && index === currentIndex) {
                                        state = 'current';
                                      }

                                      const stageMeta =
                                        state === 'completed'
                                          ? 'Past'
                                          : state === 'current'
                                          ? 'Current'
                                          : state === 'rejected'
                                          ? 'Rejected'
                                          : 'Upcoming';

                                      return (
                                        <CandidateStageSegment key={phase.id}>
                                          <CandidateStageNode>
                                            <CandidateStageHeader>
                                              <CandidateStageDot $state={state} />
                                              <CandidateStageName>{phase.name}</CandidateStageName>
                                            </CandidateStageHeader>
                                            <CandidateStageMeta>
                                              Stage {phase.order} - {stageMeta}
                                            </CandidateStageMeta>
                                            <CandidateStageScore>
                                              {phaseInterviewers.length === 0
                                                ? 'Score: --'
                                                : phaseAverage === null
                                                ? `Score: Pending (${phaseSubmittedCount}/${phaseInterviewers.length})`
                                                : `Score: ${formatScore(phaseAverage)}/10`}
                                            </CandidateStageScore>
                                          </CandidateStageNode>
                                          {index < selectedPhases.length - 1 ? (
                                            <CandidateStageConnector
                                              $active={isHired || index < currentIndex}
                                            />
                                          ) : null}
                                        </CandidateStageSegment>
                                      );
                                    })}
                                    <CandidateTimelineTotalWrap>
                                      <CandidateStageConnector $active={timelineTotalScore > 0} />
                                      <CandidateTimelineTotalNode>
                                        <CandidateTimelineTotalCircle>
                                          {formatScore(timelineTotalScore)}
                                        </CandidateTimelineTotalCircle>
                                        <CandidateStageMeta>
                                          Total score
                                          {timelineTotalMax > 0
                                            ? ` - ${formatScore(timelineTotalScore)}/${timelineTotalMax}`
                                            : ''}
                                        </CandidateStageMeta>
                                      </CandidateTimelineTotalNode>
                                    </CandidateTimelineTotalWrap>
                                  </CandidateStageTrack>
                                </CandidateStageTimeline>
                              ) : (
                                <CandidateMeta>
                                  No lifecycle stages defined for this role yet.
                                </CandidateMeta>
                              )}

                              <CandidateGrid>
                                {/* Candidate interviewer assignment is managed in Lifecycle Builder for now. */}
                                <FieldGroup>
                                  Stage scorecards
                                  {currentPhase ? (
                                    currentStageInterviewers.length > 0 ? (
                                      <CandidateScorecards>
                                        {currentStageInterviewers.map((interviewerValue) => {
                                          const matched =
                                            interviewerDirectory.get(
                                              interviewerValue.trim().toLowerCase(),
                                            ) ?? null;
                                          const identity = matched?.name || interviewerValue;
                                          const photoUrl = matched?.photoUrl || '';
                                          const reviewerScore = getStageReviewerScore(
                                            currentStageScores,
                                            interviewerValue,
                                          );
                                          return (
                                            <CandidateScorecardRow
                                              key={`${candidate.id}-${currentPhase.id}-${interviewerValue}`}
                                            >
                                              <CandidateInterviewerLabel>
                                                <PhaseInterviewerAvatar
                                                  email={matched?.email || interviewerValue}
                                                  name={identity}
                                                  photoURL={photoUrl || undefined}
                                                  size={38}
                                                />
                                                <CandidateInterviewerName>
                                                  {identity}
                                                </CandidateInterviewerName>
                                              </CandidateInterviewerLabel>
                                              <GlassPicklist
                                                value={reviewerScore}
                                                options={CANDIDATE_SCORE_OPTIONS}
                                                ariaLabel={`Select score for ${identity}`}
                                                onChange={(nextScore) => {
                                                  setCandidateDrafts((prev) => {
                                                    const current = prev[candidate.id] ?? draft;
                                                    const stageScores =
                                                      current.stageReviewerScores[currentPhase.id] ??
                                                      {};
                                                    const normalizedInterviewerKey =
                                                      interviewerValue.trim().toLowerCase();
                                                    const normalizedStageScores = Object.entries(
                                                      stageScores,
                                                    ).reduce<Record<string, string>>(
                                                      (acc, [key, value]) => {
                                                        if (
                                                          key.trim().toLowerCase() !==
                                                          normalizedInterviewerKey
                                                        ) {
                                                          acc[key] = value;
                                                        }
                                                        return acc;
                                                      },
                                                      {},
                                                    );
                                                    return {
                                                      ...prev,
                                                      [candidate.id]: {
                                                        ...current,
                                                        stageReviewerScores: {
                                                          ...current.stageReviewerScores,
                                                          [currentPhase.id]: {
                                                            ...normalizedStageScores,
                                                            [interviewerValue]: parseScoreInput(nextScore),
                                                          },
                                                        },
                                                      },
                                                    };
                                                  });
                                                }}
                                              />
                                            </CandidateScorecardRow>
                                          );
                                        })}
                                      </CandidateScorecards>
                                    ) : (
                                      <CandidateMeta>
                                        No interviewers assigned for the current stage.
                                      </CandidateMeta>
                                    )
                                  ) : (
                                    <CandidateMeta>Select a stage to enter scorecards.</CandidateMeta>
                                  )}
                                  <CandidateMeta>
                                    {currentStageInterviewers.length === 0
                                      ? 'Stage average: --'
                                      : currentStageAverage === null
                                      ? `Stage average: Pending (${currentStageSubmittedCount}/${currentStageInterviewers.length} submitted)`
                                      : `Stage average: ${formatScore(currentStageAverage)}/10`}
                                  </CandidateMeta>
                                </FieldGroup>
                              </CandidateGrid>

                              <Field>
                                Stage notes
                                <Textarea
                                  value={draft.notes}
                                  onChange={(event) =>
                                    setCandidateDrafts((prev) => ({
                                      ...prev,
                                      [candidate.id]: {
                                        ...prev[candidate.id],
                                        notes: event.target.value,
                                      },
                                    }))
                                  }
                                  placeholder="Interview notes or decision details"
                                />
                              </Field>

                              <CandidateDetailsGrid>
                                <CandidateDetailItem>
                                  <CandidateDetailLabel>Phone</CandidateDetailLabel>
                                  <CandidateDetailValue>{applicantPhone}</CandidateDetailValue>
                                </CandidateDetailItem>
                                <CandidateDetailItem>
                                  <CandidateDetailLabel>Address</CandidateDetailLabel>
                                  <CandidateDetailValue>{applicantAddress}</CandidateDetailValue>
                                </CandidateDetailItem>
                                <CandidateDetailItem>
                                  <CandidateDetailLabel>Referred by</CandidateDetailLabel>
                                  <CandidateDetailValue>{applicantReferredBy}</CandidateDetailValue>
                                </CandidateDetailItem>
                                <CandidateDetailItem>
                                  <CandidateDetailLabel>Background check</CandidateDetailLabel>
                                  <CandidateDetailValue>{backgroundCheckStatus}</CandidateDetailValue>
                                </CandidateDetailItem>
                              </CandidateDetailsGrid>

                              {linkedInUrl || portfolioUrl ? (
                                <FileLinks>
                                  {linkedInUrl ? (
                                    <FileLink href={linkedInUrl} target="_blank" rel="noreferrer">
                                      LinkedIn
                                    </FileLink>
                                  ) : null}
                                  {portfolioUrl ? (
                                    <FileLink href={portfolioUrl} target="_blank" rel="noreferrer">
                                      Portfolio
                                    </FileLink>
                                  ) : null}
                                </FileLinks>
                              ) : null}

                              {candidate.resumeDownloadUrl || candidate.coverLetterDownloadUrl ? (
                                <FileLinks>
                                  {candidate.resumeDownloadUrl ? (
                                    <FileLink
                                      href={candidate.resumeDownloadUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Resume
                                    </FileLink>
                                  ) : null}
                                  {candidate.coverLetterDownloadUrl ? (
                                    <FileLink
                                      href={candidate.coverLetterDownloadUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Cover letter
                                    </FileLink>
                                  ) : null}
                                </FileLinks>
                              ) : null}

                              <CandidateMeta>
                                Applied: {new Date(candidate.createdAt).toLocaleString()} - Current stage:{' '}
                                {currentPhase ? `Stage ${currentPhase.order} (${currentPhase.name})` : 'Unassigned'}
                              </CandidateMeta>
                              <CandidateDecisionFooter>
                                <SecondaryButton
                                  type="button"
                                  onClick={() => {
                                    if (isHired && !hasAcceptedOffer) {
                                      acceptCandidateOffer(candidate);
                                      return;
                                    }
                                    applyCandidateDecision(candidate, 'continue');
                                  }}
                                  disabled={
                                    selectedPhases.length === 0 ||
                                    isRejected ||
                                    (isHired && hasAcceptedOffer) ||
                                    candidateSavingId === candidate.id
                                  }
                                >
                                  {candidateSavingId === candidate.id ? 'Saving...' : continueLabel}
                                </SecondaryButton>
                                <CandidateDecisionFooterRight>
                                  <SecondaryButton
                                    type="button"
                                    onClick={() => saveCandidateLifecycle(candidate)}
                                    disabled={candidateSavingId === candidate.id}
                                  >
                                    {candidateSavingId === candidate.id ? 'Saving...' : 'Save details'}
                                  </SecondaryButton>
                                </CandidateDecisionFooterRight>
                              </CandidateDecisionFooter>
                            </CandidateBody>
                          ) : null}
                        </CandidateCard>
                      );
                    })
                  )}
                </Panel>
              ) : null}

              {detailsTab === 'lifecycle' ? (
                <Panel>
                  <LifecycleHeader>
                    <LifecycleHeaderCopy>
                      <PanelTitle>Job posting lifecycle</PanelTitle>
                      <FooterMeta>
                        Stage 1 is always review application. Add repeatable stages from the stage library, then reorder stages to match your hiring flow.
                      </FooterMeta>
                    </LifecycleHeaderCopy>
                    <SecondaryButton type="button" onClick={saveLifecycleStages}>
                      {saveListing.isPending ? 'Saving...' : 'Save lifecycle'}
                    </SecondaryButton>
                  </LifecycleHeader>

                  <LifecycleStagesViewport>
                    <LifecycleStagesStack>
                      {phaseDrafts.map((phase, index) => {
                        const assignedInterviewers = parseInterviewerList(phase.interviewer);
                        const assignedSet = new Set(
                          assignedInterviewers.map((value) => value.trim().toLowerCase()),
                        );
                        const normalizedQuery = phaseInterviewerQuery.trim().toLowerCase();
                        const availableStaff = stageInterviewerOptions.filter((option) => {
                          if (assignedSet.has(option.value.trim().toLowerCase())) return false;
                          if (!normalizedQuery) return true;
                          return (
                            option.name.toLowerCase().includes(normalizedQuery) ||
                            option.email.toLowerCase().includes(normalizedQuery) ||
                            option.roleLabel.toLowerCase().includes(normalizedQuery)
                          );
                        });
                        const pickerOpen = openPhaseInterviewerPickerId === phase.id;

                        return (
                          <PhaseCard key={phase.id}>
                            <PhaseHeader>
                              <PhaseTitle>Stage {index + 1}</PhaseTitle>
                              <PhaseHeaderActions>
                                {index > 1 ? (
                                  <PhaseMoveButton
                                    type="button"
                                    onClick={() => movePhaseDraft(phase.id, 'up')}
                                  >
                                    Move up
                                  </PhaseMoveButton>
                                ) : null}
                                {index > 0 && index < phaseDrafts.length - 1 ? (
                                  <PhaseMoveButton
                                    type="button"
                                    onClick={() => movePhaseDraft(phase.id, 'down')}
                                  >
                                    Move down
                                  </PhaseMoveButton>
                                ) : null}
                                {index > 0 ? (
                                  <DangerButton type="button" onClick={() => removePhaseDraft(phase.id)}>
                                    Remove
                                  </DangerButton>
                                ) : null}
                              </PhaseHeaderActions>
                            </PhaseHeader>
                            <InputGrid>
                              <Field>
                                Stage
                                {index === 0 ? (
                                  <StageFixedName>{DEFAULT_REVIEW_STAGE_NAME}</StageFixedName>
                                ) : (
                                  <GlassPicklist
                                    value={phase.name}
                                    options={
                                      (() => {
                                        const baseOptions = LIFECYCLE_STAGE_LIBRARY.map((name) => ({
                                          value: name,
                                          label: name,
                                        }));
                                        if (phase.name && !baseOptions.some((option) => option.value === phase.name)) {
                                          return [{ value: phase.name, label: phase.name }, ...baseOptions];
                                        }
                                        return baseOptions;
                                      })()
                                    }
                                    ariaLabel={`Select stage name for stage ${index + 1}`}
                                    onChange={(stageName) =>
                                      updatePhaseDraft(phase.id, { name: stageName })
                                    }
                                  />
                                )}
                              </Field>
                              <FieldGroup>
                                Interviewers
                                <PhaseInterviewersShell data-interviewer-shell="true">
                                  <PhaseInterviewersRow>
                                    {assignedInterviewers.map((interviewerValue) => {
                                      const matched =
                                        interviewerDirectory.get(interviewerValue.toLowerCase()) ?? null;
                                      const identity = matched?.name || interviewerValue;
                                      const photoUrl = matched?.photoUrl || '';
                                      return (
                                        <PhaseInterviewerChip key={`${phase.id}-${interviewerValue}`}>
                                          <PhaseInterviewerAvatar
                                            email={matched?.email || interviewerValue}
                                            name={identity}
                                            photoURL={photoUrl || undefined}
                                            size={38}
                                          />
                                          <PhaseInterviewerRemove
                                            type="button"
                                            onClick={() =>
                                              removePhaseInterviewer(phase.id, interviewerValue)
                                            }
                                            title={`Remove interviewer: ${identity}`}
                                            aria-label={`Remove interviewer ${identity}`}
                                          >
                                            x
                                          </PhaseInterviewerRemove>
                                        </PhaseInterviewerChip>
                                      );
                                    })}

                                    <PhaseAddInterviewerButton
                                      type="button"
                                      $active={pickerOpen}
                                      onClick={() => {
                                        setOpenPhaseInterviewerPickerId((current) =>
                                          current === phase.id ? null : phase.id,
                                        );
                                        setPhaseInterviewerQuery('');
                                      }}
                                      aria-label={`Add interviewer to stage ${index + 1}`}
                                      title={`Add interviewer to Stage ${index + 1}`}
                                    >
                                      +
                                    </PhaseAddInterviewerButton>
                                  </PhaseInterviewersRow>

                                  {pickerOpen ? (
                                    <PhaseInterviewerMenu>
                                      <PhaseInterviewerSearch
                                        value={phaseInterviewerQuery}
                                        onChange={(event) =>
                                          setPhaseInterviewerQuery(event.target.value)
                                        }
                                        placeholder="Search admins and agents..."
                                      />
                                      <PhaseInterviewerList>
                                        {availableStaff.length === 0 ? (
                                          <PhaseInterviewerEmpty>
                                            No available interviewers.
                                          </PhaseInterviewerEmpty>
                                        ) : (
                                          availableStaff.map((option) => (
                                            <PhaseInterviewerOption
                                              key={`${phase.id}-${option.value}`}
                                              type="button"
                                              onClick={() => {
                                                addPhaseInterviewer(phase.id, option.value);
                                                setOpenPhaseInterviewerPickerId(null);
                                                setPhaseInterviewerQuery('');
                                              }}
                                            >
                                              <PhaseInterviewerOptionTop>
                                                <PhaseInterviewerAvatar
                                                  disableNavigation
                                                  stopPropagation={false}
                                                  email={option.email}
                                                  name={option.name}
                                                  photoURL={option.photoUrl || undefined}
                                                  size={38}
                                                  title={option.name}
                                                />
                                                <PhaseInterviewerOptionName>
                                                  {option.name}
                                                </PhaseInterviewerOptionName>
                                              </PhaseInterviewerOptionTop>
                                              <PhaseInterviewerOptionMeta>
                                                {option.email} - {option.roleLabel}
                                              </PhaseInterviewerOptionMeta>
                                            </PhaseInterviewerOption>
                                          ))
                                        )}
                                      </PhaseInterviewerList>
                                    </PhaseInterviewerMenu>
                                  ) : null}
                                </PhaseInterviewersShell>
                              </FieldGroup>
                            </InputGrid>
                            <Field>
                              Stage notes
                              <Textarea
                                value={phase.notes}
                                onChange={(event) =>
                                  updatePhaseDraft(phase.id, { notes: event.target.value })
                                }
                                placeholder="Criteria or process notes"
                              />
                            </Field>
                          </PhaseCard>
                        );
                      })}
                    </LifecycleStagesStack>
                  </LifecycleStagesViewport>

                  <InlineActions>
                    <SecondaryButton type="button" onClick={addPhase}>
                      Add stage
                    </SecondaryButton>
                  </InlineActions>
                </Panel>
              ) : null}
            </DrawerBody>
          </>
        ) : null}
      </Drawer>

      <ModalBackdrop
        type="button"
        aria-label="Close create listing modal"
        $open={isCreateModalOpen}
        onClick={closeCreateModal}
      />
      <Modal $open={isCreateModalOpen}>
        <ModalHeader>
          <ModalHeaderCopy>
            <ModalTitle>Create New Listing</ModalTitle>
            <ModalSub>
              Publish a new role using the standard template. You can fully manage lifecycle stages after creation.
            </ModalSub>
          </ModalHeaderCopy>
          <CloseButton type="button" onClick={closeCreateModal}>
            Close
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {createError ? <CreateListingHelperText $error>{createError}</CreateListingHelperText> : null}
          {!createError && createMessage ? (
            <CreateListingHelperText>{createMessage}</CreateListingHelperText>
          ) : null}
          {!createError && brainTeamOptionsError ? (
            <CreateListingHelperText $error>{brainTeamOptionsError}</CreateListingHelperText>
          ) : null}

          <CreateListingForm onSubmit={createListing}>
            <InputGrid>
              <CreateListingField>
                Job title
                <CreateListingInput
                  value={createForm.title}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, title: event.target.value }))
                  }
                />
              </CreateListingField>
              <CreateListingField>
                Requisition number
                <CreateListingInput
                  value={createForm.requisitionId}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, requisitionId: event.target.value }))
                  }
                />
              </CreateListingField>
              <CreateListingField>
                Brain team node
                <CreateListingInput
                  as="select"
                  value={createForm.brainNodeId}
                  onChange={(event) => {
                    const nextBrainNodeId = event.target.value;
                    const nextTeam = brainTeamOptionById.get(nextBrainNodeId)?.teamLabel ?? '';
                    setCreateForm((prev) => ({
                      ...prev,
                      brainNodeId: nextBrainNodeId,
                      team: nextTeam,
                    }));
                  }}
                  disabled={brainTeamOptionsLoading}
                >
                  <option value="">
                    {brainTeamOptionsLoading
                      ? 'Loading team nodes...'
                      : brainTeamOptions.length
                        ? 'Select Brain team node'
                        : 'No active team nodes found'}
                  </option>
                  {brainTeamOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </CreateListingInput>
              </CreateListingField>
              <CreateListingField>
                Team
                <CreateListingInput
                  value={createForm.team}
                  readOnly
                  placeholder="Select a Brain team node"
                />
              </CreateListingField>
              <CreateListingField>
                Location
                <CreateListingInput
                  value={createForm.location}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, location: event.target.value }))
                  }
                />
              </CreateListingField>
              <CreateListingField>
                Work type
                <GlassPicklist
                  value={createForm.workType}
                  options={CREATE_LISTING_WORK_TYPE_OPTIONS}
                  ariaLabel="Select work type"
                  onChange={(workType) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      workType,
                    }))
                  }
                />
              </CreateListingField>
              <CreateListingField>
                Publish now?
                <GlassPicklist
                  value={createForm.published ? 'yes' : 'no'}
                  options={CREATE_LISTING_PUBLISH_OPTIONS}
                  ariaLabel="Select publish status"
                  onChange={(publishValue) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      published: publishValue === 'yes',
                    }))
                  }
                />
              </CreateListingField>
            </InputGrid>

            <CreateListingField>
              Summary
              <CreateListingTextarea
                value={createForm.summary}
                onChange={(event) =>
                  setCreateForm((prev) => ({ ...prev, summary: event.target.value }))
                }
              />
            </CreateListingField>

            <CreateListingField>
              Description
              <CreateListingTextarea
                value={createForm.description}
                onChange={(event) =>
                  setCreateForm((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </CreateListingField>

            <InputGrid>
              <CreateListingField>
                Responsibilities (one per line)
                <CreateListingTextarea
                  value={createForm.responsibilities}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      responsibilities: event.target.value,
                    }))
                  }
                />
              </CreateListingField>
              <CreateListingField>
                Requirements (one per line)
                <CreateListingTextarea
                  value={createForm.requirements}
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, requirements: event.target.value }))
                  }
                />
              </CreateListingField>
            </InputGrid>

            <InlineActions>
              <CreateListingSubmitButton type="submit" disabled={postCareerJob.isPending}>
                {postCareerJob.isPending ? 'Creating...' : 'Create listing'}
              </CreateListingSubmitButton>
            </InlineActions>
          </CreateListingForm>
        </ModalBody>
      </Modal>
    </>
  );
}



